/**
 * /ad3 — The complete NextBell customer playbook.
 * Every screen, every tap, broken out one section per surface.
 */
import { motion } from 'framer-motion';
import {
  Phone, Users, Calendar, MessageSquare, Mail, BarChart3, Settings, Bot,
  Workflow, BookOpen, ChevronRight, Check, Lock, Sparkles, Shield,
  TrendingUp, Clock, ArrowRight, Star, Globe, Smartphone, CreditCard,
  CheckCircle2, X, Send, Edit3, Trash2, Plus, Power, Play, Save,
  Search, Building2, Zap, GitBranch, FileText, AlertTriangle, History,
  Camera, Eye, RotateCcw, Wand2, Upload, Download, Key, Bell, LogOut,
  PhoneOff, HelpCircle, Wifi, MapPin, AlertCircle, RefreshCw, ListPlus,
  CircleCheck, Headphones, MessageCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/* ─────────────── PRIMITIVES ─────────────── */
function StepBadge({ n }) {
  return (
    <div className="inline-flex items-center gap-2 mb-3">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-blue-400">Step</span>
      <span className="text-[11px] font-semibold tracking-wide bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-lg shadow-blue-500/25">
        {String(n).padStart(2, '0')}
      </span>
    </div>
  );
}
function SubStepBadge({ label }) {
  return (
    <span className="inline-block text-[10px] uppercase tracking-[0.22em] text-blue-400 font-semibold mb-2 bg-blue-500/[0.06] border border-blue-500/20 rounded-full px-2.5 py-0.5">{label}</span>
  );
}
function SectionHeader({ kicker, title, sub }) {
  return (
    <div className="max-w-3xl mb-10">
      {kicker && <p className="text-[11px] uppercase tracking-[0.25em] text-blue-400 font-semibold mb-3">{kicker}</p>}
      <h2 className="font-heading text-3xl sm:text-4xl font-medium text-white leading-tight mb-3">{title}</h2>
      {sub && <p className="text-slate-400 text-base leading-relaxed">{sub}</p>}
    </div>
  );
}
function GlassPanel({ children, className = '' }) {
  return <div className={`bg-white/[0.025] border border-white/[0.06] rounded-2xl ${className}`}>{children}</div>;
}
function BrowserChrome({ url, children }) {
  return (
    <div className="relative bg-[#0d0e10] border border-white/[0.06] rounded-2xl overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
      <div className="flex items-center gap-2 px-3 py-2.5 bg-white/[0.02] border-b border-white/[0.05]">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
        <span className="w-2.5 h-2.5 rounded-full bg-blue-500/80" />
        <span className="ml-3 text-[10.5px] sm:text-[11px] text-slate-500 font-mono inline-flex items-center gap-1.5 truncate"><Lock className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{url}</span></span>
      </div>
      {children}
    </div>
  );
}
function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2.5 text-[13.5px] text-slate-300 leading-relaxed">
      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
      <span>{children}</span>
    </li>
  );
}
function Tip({ children }) {
  return (
    <div className="flex items-start gap-2.5 bg-amber-500/[0.08] border border-amber-500/20 rounded-lg p-3 text-[12.5px] text-amber-100/90 leading-relaxed">
      <Sparkles className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}
function Connects({ children }) {
  return (
    <div className="flex items-start gap-2.5 bg-blue-500/[0.06] border border-blue-500/20 rounded-lg p-3 text-[12.5px] text-blue-100/90 leading-relaxed">
      <GitBranch className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}
function Section({ n, sub, kicker, title, lede, mock, instructions, why, tip, connects }) {
  return (
    <section className="py-14 sm:py-20 border-t border-white/[0.04]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {n && <StepBadge n={n} />}
        {sub && <SubStepBadge label={sub} />}
        <SectionHeader kicker={kicker} title={title} sub={lede} />
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="lg:col-span-3">
            {mock}
          </motion.div>
          <div className="lg:col-span-2 space-y-4">
            {instructions && (
              <div>
                <h4 className="text-white text-sm font-semibold uppercase tracking-wider mb-3">Every tap, in order</h4>
                <ul className="space-y-2.5">{instructions.map((b, i) => <Bullet key={i}>{b}</Bullet>)}</ul>
              </div>
            )}
            {why && (
              <div>
                <h4 className="text-white text-sm font-semibold uppercase tracking-wider mb-3">Why it matters</h4>
                <p className="text-[13.5px] text-slate-400 leading-relaxed">{why}</p>
              </div>
            )}
            {connects && <Connects>{connects}</Connects>}
            {tip && <Tip>{tip}</Tip>}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────── SIGNUP WIZARD — 5 dedicated mocks ─────────────── */
function WizShell({ step, total = 5, title, lede, children }) {
  return (
    <BrowserChrome url="nextbell.ai/signup">
      <div className="p-6 sm:p-10 bg-[#0a0a0a]">
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-2 mb-6">
            <div className="flex items-center gap-1">
              {Array.from({ length: total }).map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full ${i + 1 <= step ? 'bg-blue-500 w-8' : 'bg-white/10 w-5'}`} />
              ))}
            </div>
            <span className="ml-auto text-[11px] text-slate-500">Step {step} of {total}</span>
          </div>
          <h3 className="text-white font-heading text-2xl font-medium mb-1.5">{title}</h3>
          <p className="text-slate-400 text-[13px] mb-6">{lede}</p>
          {children}
          <div className="flex gap-2 mt-7">
            {step > 1 && <button className="text-[13px] text-slate-300 px-4 py-2.5 rounded-lg border border-white/10">Back</button>}
            <button className="flex-1 bg-blue-600 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg inline-flex items-center justify-center gap-1.5">
              {step === total ? 'Activate my account' : 'Continue'} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </BrowserChrome>
  );
}
function Field({ label, value, optional, focused, icon: Icon }) {
  return (
    <div>
      <label className="text-[10.5px] uppercase tracking-wide text-slate-400 font-medium">{label} {optional && <span className="text-slate-500 normal-case">(optional)</span>}</label>
      <div className={`bg-white/[0.04] border ${focused ? 'border-blue-500/50' : 'border-white/10'} rounded-lg px-3 py-2.5 text-[13px] text-white mt-1 flex items-center gap-2`}>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-500" />} {value}
      </div>
    </div>
  );
}
function SignupStep1Mock() {
  return (
    <WizShell step={1} title="Create your account" lede="Just your name, email, and a strong password to get started.">
      <div className="space-y-3.5">
        <Field label="Your full name" value="Alex Rivera" focused />
        <Field label="Email address" value="alex@luxehair.com" />
        <Field label="Password" value="••••••••••••" />
        <div className="flex items-start gap-2 mt-2">
          <span className="w-4 h-4 rounded border border-blue-500 bg-blue-500/20 flex items-center justify-center mt-0.5"><Check className="w-2.5 h-2.5 text-blue-300" /></span>
          <span className="text-[12px] text-slate-300 leading-relaxed">I agree to the <span className="text-blue-400">Terms</span> and <span className="text-blue-400">Privacy Policy</span></span>
        </div>
      </div>
    </WizShell>
  );
}
function SignupStep2Mock() {
  return (
    <WizShell step={2} title="Tell us about your business" lede="We use this to brand Ava and personalise your dashboard.">
      <div className="space-y-3.5">
        <Field label="Business name *" value="Luxe Hair Studio" focused />
        <div>
          <label className="text-[10.5px] uppercase tracking-wide text-slate-400 font-medium">Industry *</label>
          <div className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 text-[13px] text-white mt-1 flex items-center justify-between">
            <span className="flex items-center gap-2">🪒 Salon / Spa</span>
            <ChevronRight className="w-3.5 h-3.5 text-slate-500 rotate-90" />
          </div>
        </div>
        <Field label="Business phone *" value="+1 (415) 555-0184" />
        <Field label="Website" value="luxehair.com" optional icon={Globe} />
      </div>
    </WizShell>
  );
}
function SignupStep3Mock() {
  return (
    <WizShell step={3} title="How does Ava sound?" lede="Greeting style, languages, and any FAQ Ava should always know.">
      <div className="space-y-3.5">
        <div>
          <label className="text-[10.5px] uppercase tracking-wide text-slate-400 font-medium">Greeting style *</label>
          <div className="grid grid-cols-2 gap-1.5 mt-1.5">
            {[{l:'Friendly + warm',pick:true},{l:'Professional'},{l:'Luxury / formal'},{l:'Casual'}].map((g,i)=>(
              <div key={i} className={`text-[12px] rounded-lg px-2.5 py-2 border text-center ${g.pick?'bg-blue-500/15 border-blue-500/40 text-blue-200':'bg-white/[0.03] border-white/10 text-slate-300'}`}>{g.l}</div>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-wide text-slate-400 font-medium">Languages spoken *</label>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            <span className="bg-blue-500/15 text-blue-300 border border-blue-500/30 rounded-full text-[11px] px-2.5 py-1 inline-flex items-center gap-1">English <Check className="w-2.5 h-2.5" /></span>
            <span className="bg-blue-500/15 text-blue-300 border border-blue-500/30 rounded-full text-[11px] px-2.5 py-1 inline-flex items-center gap-1">French <Check className="w-2.5 h-2.5" /></span>
            <span className="text-[11px] text-slate-500 border border-white/10 border-dashed rounded-full px-2.5 py-1 inline-flex items-center gap-1"><Plus className="w-2.5 h-2.5" /> Add</span>
          </div>
        </div>
        <Field label="Top FAQ Ava should know" value="Walk-ins are welcome — book ahead for color services" />
      </div>
    </WizShell>
  );
}
function SignupStep4Mock() {
  const plans = [
    { name: 'Pay As You Go', price: '$0', sub: '$0.45/min · $0.05/text · pay only for use' },
    { name: 'Starter',    price: '$179.99', sub: '250 mins · 100 SMS · 1 location' },
    { name: 'Growth',     price: '$279.99', sub: '500 mins · 250 SMS · full automation', pick: true, badge: 'Most popular' },
    { name: 'Agency',     price: '$1497', sub: '2,000 mins · 1,000 SMS · up to 5 locations · white label' },
  ];
  return (
    <WizShell step={4} title="Pick your plan" lede="Switch any time. 7-day free trial, no card required up front. Save ~17% on annual billing.">
      <div className="space-y-2.5">
        {plans.map((p, i) => (
          <div key={i} className={`rounded-xl border p-3 flex items-center gap-3 ${p.pick ? 'bg-blue-500/[0.07] border-blue-500/40' : 'bg-white/[0.03] border-white/10'}`}>
            <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${p.pick ? 'border-blue-500 bg-blue-500' : 'border-slate-500'}`}>
              {p.pick && <span className="block w-1.5 h-1.5 bg-white rounded-full m-auto mt-[3px]" />}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-white font-medium text-[13px]">{p.name}</p>
                {p.badge && <span className="text-[9px] uppercase tracking-wide bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded-full px-1.5 py-0.5">{p.badge}</span>}
              </div>
              <p className="text-slate-500 text-[11px] mt-0.5 truncate">{p.sub}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-white font-heading text-lg tabular-nums">{p.price}</p>
              <p className="text-slate-500 text-[10px]">{p.price === 'Custom' ? 'contact sales' : '/ month'}</p>
            </div>
          </div>
        ))}
      </div>
    </WizShell>
  );
}
function SignupStep5Mock() {
  return (
    <WizShell step={5} title="Ready to launch" lede="Review and activate. Ava is provisioning your phone number now.">
      <div className="space-y-2.5">
        {[
          { l: 'Account', v: 'alex@luxehair.com' },
          { l: 'Business', v: 'Luxe Hair Studio · Salon' },
          { l: 'Business phone', v: '+1 (415) 555-0184' },
          { l: 'Greeting', v: 'Friendly + warm · EN, FR' },
          { l: 'Plan', v: 'Growth · $279.99/mo · 7-day free trial' },
          { l: 'Your NextBell number', v: '+1 (415) 555-9070', highlight: true },
        ].map((row, i) => (
          <div key={i} className={`flex items-center justify-between rounded-lg px-3 py-2.5 border ${row.highlight ? 'bg-blue-500/[0.07] border-blue-500/30' : 'bg-white/[0.03] border-white/10'}`}>
            <span className="text-[10.5px] uppercase tracking-wide text-slate-500 font-medium">{row.l}</span>
            <span className={`text-[12.5px] ${row.highlight ? 'text-blue-300 font-semibold' : 'text-white'}`}>{row.v}</span>
          </div>
        ))}
        <div className="bg-blue-500/[0.05] border border-blue-500/20 rounded-lg p-2.5 text-[11.5px] text-blue-100/90 mt-3">
          ✨ Your dedicated phone number is ready. Forward your business line to it any time.
        </div>
      </div>
    </WizShell>
  );
}

/* ─────────────── LOGIN MOCKS ─────────────── */
function LoginMock() {
  return (
    <BrowserChrome url="nextbell.ai/login">
      <div className="p-6 sm:p-10 bg-[#0a0a0a] min-h-[420px] flex items-center justify-center">
        <div className="max-w-sm w-full">
          <h3 className="text-white font-heading text-2xl font-medium mb-1.5">Sign in</h3>
          <p className="text-slate-400 text-[13px] mb-6">Welcome back, Alex.</p>
          <div className="space-y-3">
            <Field label="Email" value="alex@luxehair.com" />
            <Field label="Password" value="•••••••••••••" />
            <div className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 text-[13px] text-white flex items-center gap-2.5">
              <span className="w-4 h-4 rounded border border-blue-500 bg-blue-500/20 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-blue-300" /></span>
              I am human
            </div>
            <p className="text-right text-[11.5px] text-blue-400">Forgot your password?</p>
          </div>
          <button className="w-full mt-3 bg-blue-600 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg">Continue</button>
        </div>
      </div>
    </BrowserChrome>
  );
}
function TwoFAMock() {
  return (
    <BrowserChrome url="nextbell.ai/login">
      <div className="p-6 sm:p-10 bg-[#0a0a0a] min-h-[420px] flex items-center justify-center">
        <div className="max-w-sm w-full">
          <div className="flex items-center gap-2.5 mb-1.5">
            <div className="w-8 h-8 rounded-lg bg-purple-500/15 border border-purple-500/30 flex items-center justify-center"><Mail className="w-3.5 h-3.5 text-purple-300" /></div>
            <h3 className="text-white font-heading text-xl font-medium">Check your email</h3>
          </div>
          <p className="text-slate-400 text-[13px] mb-6">We sent a 6-digit code to <span className="text-white">alex@luxehair.com</span>. It expires in 10 minutes.</p>
          <div className="flex gap-2 mb-4">
            {['7','3','9','0','4','2'].map((d,i)=>(
              <div key={i} className="flex-1 aspect-square bg-white/[0.04] border border-blue-500/40 rounded-lg flex items-center justify-center text-white font-heading text-xl">{d}</div>
            ))}
          </div>
          <button className="w-full bg-blue-600 text-white text-[13px] font-medium px-4 py-2.5 rounded-lg">Verify and sign in</button>
          <p className="text-center text-[11.5px] text-slate-500 mt-4">Didn't get it? <span className="text-blue-400">Resend</span></p>
        </div>
      </div>
    </BrowserChrome>
  );
}

/* ─────────────── DASHBOARD SHELL ─────────────── */
function DashShell({ active, children }) {
  const items = [
    { id: 'home', label: 'Dashboard', icon: TrendingUp },
    { id: 'calls', label: 'Calls', icon: Phone },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'appointments', label: 'Appointments', icon: Calendar },
    { id: 'messaging', label: 'Messaging', icon: MessageSquare },
    { id: 'ava', label: 'Ava', icon: Bot },
    { id: 'automations', label: 'Automations', icon: Workflow },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];
  const url = active === 'home' ? 'app.nextbell.ai/dashboard' : `app.nextbell.ai/dashboard/${active}`;
  return (
    <BrowserChrome url={url}>
      <div className="bg-[#0a0a0a] flex" style={{ minHeight: 420 }}>
        {/* Sidebar — hidden on phones, compact on tablets, full on desktop */}
        <aside className="hidden sm:flex flex-col w-[120px] lg:w-[160px] border-r border-white/[0.05] py-4 px-2 flex-shrink-0">
          <div className="flex items-center gap-2 px-2 mb-4">
            <div className="w-5 h-5 rounded bg-gradient-to-br from-blue-500 to-purple-600" />
            <span className="text-white font-heading font-semibold text-sm">NextBell</span>
          </div>
          {items.map((it) => {
            const Icon = it.icon;
            const isActive = it.id === active;
            return (
              <div key={it.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-md mb-0.5 text-[11px] lg:text-[11.5px] ${isActive ? 'bg-blue-500/15 text-blue-300 font-medium' : 'text-slate-400'}`}>
                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{it.label}</span>
              </div>
            );
          })}
        </aside>
        {/* Mobile-only top tab indicator */}
        <div className="sm:hidden absolute top-12 left-0 right-0 z-10 bg-[#0d0e10]/90 backdrop-blur border-b border-white/[0.06] px-3 py-2 flex items-center gap-2 text-[11px] text-blue-300">
          {(() => { const cur = items.find(i => i.id === active); const Icon = cur?.icon || TrendingUp; return <><Icon className="w-3 h-3" /> {cur?.label || 'Dashboard'}</>; })()}
        </div>
        <main className="flex-1 p-3 sm:p-5 min-w-0 pt-12 sm:pt-5">{children}</main>
      </div>
    </BrowserChrome>
  );
}

/* ─────────────── ONBOARDING — 5 sub-mocks ─────────────── */
function OnboardingShell({ step, total = 5, title, sub, children }) {
  return (
    <DashShell active="home">
      <div className="bg-gradient-to-br from-blue-500/[0.05] to-purple-500/[0.05] border border-blue-500/20 rounded-2xl p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
          <div className="flex-1">
            <h3 className="text-white font-heading text-base font-medium">{title}</h3>
            <p className="text-slate-400 text-[12px]">{sub}</p>
          </div>
          <span className="text-[11px] text-slate-500">{step} of {total}</span>
        </div>
        <div className="flex gap-1 mb-5">
          {Array.from({ length: total }).map((_, i) => (
            <span key={i} className={`flex-1 h-1 rounded-full ${i + 1 <= step ? 'bg-blue-500' : 'bg-white/10'}`} />
          ))}
        </div>
        {children}
      </div>
    </DashShell>
  );
}
function Ob1Greeting() {
  return (
    <OnboardingShell step={1} title="Greeting tone" sub="How does Ava sound on her very first 'Hello'?">
      <div className="grid grid-cols-2 gap-2">
        {[
          { l: 'Friendly + warm', d: 'Casual, conversational, smiles through the phone', pick: true },
          { l: 'Professional', d: 'Polished, businesslike, brisk' },
          { l: 'Luxury / formal', d: '"Good afternoon, Madam" energy' },
          { l: 'Casual / playful', d: 'Light, fun, a touch of personality' },
        ].map((o, i) => (
          <div key={i} className={`rounded-lg p-3 border ${o.pick ? 'bg-blue-500/[0.08] border-blue-500/40' : 'bg-white/[0.03] border-white/10'}`}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-white text-[12.5px] font-medium">{o.l}</p>
              {o.pick && <Check className="w-3.5 h-3.5 text-blue-300" />}
            </div>
            <p className="text-slate-500 text-[10.5px] leading-snug">{o.d}</p>
          </div>
        ))}
      </div>
    </OnboardingShell>
  );
}
function Ob2Languages() {
  return (
    <OnboardingShell step={2} title="Languages" sub="Ava picks up automatically — pick everything your customers might speak.">
      <div className="flex flex-wrap gap-1.5">
        {[
          ['English',true],['French',true],['Spanish',true],['Mandarin',false],['Arabic',false],['German',false],['Portuguese',false],['Italian',false],['Japanese',false],['Korean',false],['Russian',false],
        ].map(([l, on], i) => (
          <span key={i} className={`text-[11px] rounded-full px-2.5 py-1 border inline-flex items-center gap-1 ${on ? 'bg-blue-500/15 text-blue-300 border-blue-500/30' : 'bg-white/[0.03] text-slate-400 border-white/10'}`}>
            {l} {on && <Check className="w-2.5 h-2.5" />}
          </span>
        ))}
      </div>
    </OnboardingShell>
  );
}
function Ob3Services() {
  return (
    <OnboardingShell step={3} title="Services & pricing" sub="Everything you charge for. Ava quotes from this list.">
      <div className="space-y-2">
        {[
          { n: 'Cut & style', p: 60, t: 45 },
          { n: 'Color', p: 120, t: 90 },
          { n: 'Balayage', p: 250, t: 180 },
          { n: 'Highlights', p: 180, t: 120 },
          { n: 'Treatment', p: 75, t: 60 },
        ].map((s, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center bg-white/[0.03] border border-white/10 rounded-lg p-2 text-[12px]">
            <span className="col-span-5 text-white">{s.n}</span>
            <span className="col-span-3 text-slate-300 text-right">${s.p}</span>
            <span className="col-span-3 text-slate-300 text-right">{s.t} min</span>
            <Trash2 className="col-span-1 w-3 h-3 text-slate-600 mx-auto" />
          </div>
        ))}
        <button className="text-[11px] text-blue-300 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add service</button>
      </div>
    </OnboardingShell>
  );
}
function Ob4Hours() {
  const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  return (
    <OnboardingShell step={4} title="Business hours" sub="Ava only books inside these hours. Set holidays in Settings later.">
      <div className="space-y-1.5">
        {days.map((d,i)=>{
          const closed = i === 6;
          return (
            <div key={d} className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-[12px]">
              <span className="w-12 text-white font-medium">{d}</span>
              <span className={`w-9 h-5 rounded-full ${closed ? 'bg-white/10' : 'bg-blue-500/40'} relative`}>
                <span className={`absolute top-0.5 ${closed ? 'left-0.5' : 'left-4'} w-4 h-4 rounded-full bg-white`} />
              </span>
              {closed ? <span className="text-slate-500 text-[11px]">Closed</span> : <span className="text-slate-300">9:00 am — 7:00 pm</span>}
            </div>
          );
        })}
      </div>
    </OnboardingShell>
  );
}
function Ob5Intake() {
  return (
    <OnboardingShell step={5} title="Intake questions" sub="What does Ava always ask new customers? She'll save the answers to the lead profile.">
      <div className="space-y-2">
        {[
          { q: 'How did you hear about us?', on: true },
          { q: 'Hair length?', on: true },
          { q: 'Have you been here before?', on: true },
          { q: 'Any allergies or scalp sensitivities?', on: true },
          { q: 'Preferred stylist (if any)?', on: false },
        ].map((it, i) => (
          <div key={i} className="flex items-center gap-2.5 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-[12px]">
            <span className={`w-9 h-5 rounded-full ${it.on ? 'bg-blue-500/40' : 'bg-white/10'} relative`}>
              <span className={`absolute top-0.5 ${it.on ? 'left-4' : 'left-0.5'} w-4 h-4 rounded-full bg-white`} />
            </span>
            <span className="text-white flex-1">{it.q}</span>
            <Edit3 className="w-3 h-3 text-slate-600" />
          </div>
        ))}
      </div>
    </OnboardingShell>
  );
}

/* ─────────────── DASHBOARD HOME ─────────────── */
function DashHomeMock() {
  return (
    <DashShell active="home">
      <div className="space-y-3">
        <div className="bg-gradient-to-br from-blue-500/[0.07] to-purple-500/[0.07] border border-blue-500/20 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-wider text-blue-300 font-semibold">Today's daily brief</span>
            <span className="text-[10px] text-slate-500">8:00 AM · Friday Feb 14</span>
          </div>
          <p className="text-[12.5px] text-slate-100 leading-relaxed">Good morning, Alex. Ava handled <strong className="text-white">14 calls</strong> overnight, booked <strong className="text-white">3 appointments</strong>, and flagged <strong className="text-amber-300">1 hot lead</strong> who&apos;s ready for a callback. Two callers asked about wedding packages, you may want to add that to your Knowledge Base.</p>
        </div>
        <div className="grid grid-cols-4 gap-2.5">
          {[
            { label: 'Calls today', value: '23', delta: '+18%' },
            { label: 'New leads', value: '8', delta: '+12%' },
            { label: 'Booked', value: '5', delta: '+25%' },
            { label: 'Won', value: '$1,240', delta: '+8%' },
          ].map((k) => (
            <div key={k.label} className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-2.5">
              <p className="text-[9.5px] uppercase tracking-wider text-slate-500 font-medium">{k.label}</p>
              <p className="text-white font-heading text-xl font-medium tabular-nums mt-0.5">{k.value}</p>
              <p className="text-[10px] text-blue-400 mt-0.5">{k.delta}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3.5">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Activity feed</p>
            <ul className="space-y-1.5 text-[11.5px]">
              {[
                { who: 'Ava', what: 'booked Sarah M. for balayage Sat 2pm', t: '2m' },
                { who: 'Ava', what: 'scored Mike T. as HOT (94/100)', t: '14m' },
                { who: 'Ava', what: 'sent reminders to 5 appointments', t: '1h' },
                { who: 'Ava', what: 'launched re-engagement for 12 cold leads', t: '3h' },
              ].map((r, i) => (
                <li key={i} className="flex items-center gap-2 text-slate-300">
                  <span className="text-[10px] text-blue-300 font-medium w-9">{r.who}</span>
                  <span className="flex-1 truncate">{r.what}</span>
                  <span className="text-[10px] text-slate-500">{r.t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3.5">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Needs your attention</p>
            <ul className="space-y-2 text-[11.5px]">
              <li className="flex items-start gap-2 text-slate-300">
                <AlertCircle className="w-3 h-3 text-amber-400 flex-shrink-0 mt-0.5" />
                <span>Mike Thompson left a voicemail — Ava marked it urgent.</span>
              </li>
              <li className="flex items-start gap-2 text-slate-300">
                <AlertCircle className="w-3 h-3 text-blue-400 flex-shrink-0 mt-0.5" />
                <span>2 callers asked about &quot;wedding packages&quot; — not in KB yet.</span>
              </li>
              <li className="flex items-start gap-2 text-slate-300">
                <CheckCircle2 className="w-3 h-3 text-blue-400 flex-shrink-0 mt-0.5" />
                <span>Calendar synced 4 minutes ago.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </DashShell>
  );
}

/* ─────────────── CALLS ─────────────── */
function CallsMock() {
  const calls = [
    { name: 'Sarah Mitchell', phone: '+1 (415) 555-0184', dur: '4m 12s', outcome: 'Booked', tone: 'emerald', summary: 'Asked about balayage. Ava quoted $250 starting and offered Saturday 2pm. Confirmed.' },
    { name: 'David Park', phone: '+1 (628) 555-0177', dur: '2m 45s', outcome: 'Quoted', tone: 'blue', summary: 'Wedding party of 6 — Ava sent the wedding packages PDF and offered a free consultation.' },
    { name: 'Lisa Nguyen', phone: '+1 (415) 555-0102', dur: '1m 02s', outcome: 'Voicemail', tone: 'amber', summary: 'Caller hung up after greeting. Ava sent a follow-up SMS automatically.' },
    { name: 'Mike Thompson', phone: '+1 (510) 555-0144', dur: '6m 30s', outcome: 'Transfer', tone: 'purple', summary: 'Refund question — Ava transferred to you per your refund-policy rule.' },
  ];
  const tone = { emerald: 'bg-blue-500/15 text-blue-300 border-blue-500/30', blue: 'bg-blue-500/15 text-blue-300 border-blue-500/30', amber: 'bg-amber-500/15 text-amber-300 border-amber-500/30', purple: 'bg-purple-500/15 text-purple-300 border-purple-500/30' };
  return (
    <DashShell active="calls">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white font-heading text-base font-semibold">Calls</h2>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="bg-white/[0.04] border border-white/10 rounded-full px-2.5 py-1 text-slate-300 inline-flex items-center gap-1"><Search className="w-2.5 h-2.5" /> Search</span>
          <span className="bg-white/[0.04] border border-white/10 rounded-full px-2.5 py-1 text-slate-300">Last 7 days</span>
          <span className="bg-white/[0.04] border border-white/10 rounded-full px-2.5 py-1 text-slate-300">All outcomes</span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[{l:'Total calls',v:'342'},{l:'Minutes used',v:'847 / 1,200'},{l:'With recording',v:'318'}].map((k)=>(
          <div key={k.l} className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-2.5">
            <p className="text-[9.5px] uppercase tracking-wider text-slate-500 font-medium">{k.l}</p>
            <p className="text-white font-heading text-base font-medium tabular-nums mt-0.5">{k.v}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {calls.map((c, i) => (
          <div key={i} className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">{c.dur}</span>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${tone[c.tone]}`}>{c.outcome}</span>
            </div>
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-[10px] font-semibold">
                {c.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <p className="text-white text-[12px] font-medium">{c.name}</p>
                <p className="text-slate-500 text-[10px]">{c.phone}</p>
              </div>
            </div>
            <p className="text-slate-400 text-[10.5px] leading-snug italic line-clamp-2">&quot;{c.summary}&quot;</p>
          </div>
        ))}
      </div>
    </DashShell>
  );
}
function CallReplayMock() {
  return (
    <BrowserChrome url="app.nextbell.ai/dashboard/calls/c_8af2…">
      <div className="bg-[#0a0a0a] p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-[12px] font-semibold">SM</div>
          <div className="flex-1">
            <p className="text-white font-medium">Sarah Mitchell</p>
            <p className="text-slate-500 text-[11px]">+1 (415) 555-0184 · 4m 12s · Booked</p>
          </div>
          <span className="text-[10px] uppercase tracking-wider bg-blue-500/15 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded-full">Booked</span>
        </div>
        <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3 mb-3 flex items-center gap-3">
          <span className="w-8 h-8 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center"><Play className="w-3 h-3 text-blue-300" /></span>
          <div className="flex-1 h-1.5 bg-white/[0.05] rounded-full">
            <div className="h-full bg-blue-500 rounded-full" style={{ width: '34%' }} />
          </div>
          <span className="text-slate-400 text-[11px] tabular-nums">1:24 / 4:12</span>
        </div>
        <div className="bg-purple-500/[0.06] border border-purple-500/20 rounded-xl p-3 mb-3">
          <p className="text-[10px] uppercase tracking-wider text-purple-300 font-semibold mb-2">Conversation replay</p>
          <ol className="space-y-2 relative pl-4 border-l border-white/10 ml-1">
            {[
              { l: 'Caller intent', d: 'Booking', tone: 'blue' },
              { l: 'Engine routed', d: 'Ava asks: &quot;What service today?&quot;', tone: 'purple' },
              { l: 'Caller intent', d: 'Pricing', tone: 'blue' },
              { l: 'Engine routed', d: 'Lookup KB: &quot;Balayage starts at $250…&quot;', tone: 'purple' },
              { l: 'Engine routed', d: 'Collect & book → Saturday 2pm', tone: 'purple' },
              { l: 'Engine ended', d: 'Booking confirmed, wrap-up', tone: 'emerald' },
            ].map((ev, i) => (
              <li key={i} className="relative">
                <span className={`absolute -left-5 top-0.5 w-3 h-3 rounded-full border-2 bg-[#0a0a0a] ${ev.tone === 'blue' ? 'border-blue-500' : ev.tone === 'purple' ? 'border-purple-500' : 'border-blue-500'}`} />
                <p className="text-[11px] text-slate-300"><span className="text-slate-500 mr-1.5">{ev.l}:</span><span dangerouslySetInnerHTML={{ __html: ev.d }} /></p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </BrowserChrome>
  );
}

/* ─────────────── LEADS ─────────────── */
function LeadsBoardMock() {
  const cols = [
    { name: 'New', color: 'slate', leads: [{ n: 'Mike Thompson', s: 51 }] },
    { name: 'Contacted', color: 'blue', leads: [{ n: 'Lisa Nguyen', s: 64 }, { n: 'Ana Cruz', s: 70 }] },
    { name: 'Qualified', color: 'purple', leads: [{ n: 'David Park', s: 78 }] },
    { name: 'Booked', color: 'emerald', leads: [{ n: 'Sarah Mitchell', s: 92 }, { n: 'Tom Lee', s: 81 }] },
    { name: 'Won', color: 'amber', leads: [] },
  ];
  return (
    <DashShell active="leads">
      <h2 className="text-white font-heading text-base font-semibold mb-3">Leads pipeline</h2>
      <div className="grid grid-cols-5 gap-1.5">
        {cols.map((c, i) => (
          <div key={i} className="bg-white/[0.02] border border-white/[0.05] rounded-xl p-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10.5px] uppercase tracking-wider text-slate-300 font-semibold">{c.name}</p>
              <span className="text-[10px] text-slate-500">{c.leads.length}</span>
            </div>
            <div className="space-y-1.5">
              {c.leads.map((l, j) => (
                <div key={j} className="bg-white/[0.04] border border-white/10 rounded-md p-1.5">
                  <p className="text-white text-[10.5px] truncate">{l.n}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <div className="flex-1 h-1 bg-white/[0.06] rounded-full overflow-hidden">
                      <div className={`h-full ${l.s >= 80 ? 'bg-blue-500' : l.s >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${l.s}%` }} />
                    </div>
                    <span className="text-[9px] text-slate-400 tabular-nums">{l.s}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </DashShell>
  );
}
function LeadDetailMock() {
  return (
    <DashShell active="leads">
      <div className="flex items-center gap-3 mb-3">
        <ChevronRight className="w-3.5 h-3.5 text-slate-500 rotate-180" />
        <p className="text-white font-heading text-sm">Sarah Mitchell</p>
        <span className="text-[10px] uppercase tracking-wider bg-blue-500/15 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded-full">Booked</span>
        <span className="ml-auto text-[10px] text-slate-500">Score: 92/100 (HOT)</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-1 space-y-2">
          <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Profile</p>
            <p className="text-white text-[12px] font-medium">Sarah Mitchell</p>
            <p className="text-slate-400 text-[11px]">+1 (415) 555-0184</p>
            <p className="text-slate-400 text-[11px]">sarah@gmail.com</p>
            <p className="text-slate-500 text-[10.5px] mt-2">Source: Inbound call · Saved by: Ava</p>
          </div>
          <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Tags</p>
            <div className="flex flex-wrap gap-1">
              {['hot','wedding-prospect','referred-by-jess'].map(t=><span key={t} className="text-[10px] bg-white/[0.05] border border-white/10 rounded-full px-2 py-0.5 text-slate-300">{t}</span>)}
            </div>
          </div>
        </div>
        <div className="col-span-2 space-y-2">
          <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Timeline</p>
            <ul className="space-y-2 text-[11px]">
              {[
                { who: 'Ava', what: 'Booked balayage Saturday 2pm', t: '2m ago' },
                { who: 'Ava', what: 'Quoted balayage starting at $250', t: '4m ago' },
                { who: 'Sarah', what: 'Inbound call — 4m 12s', t: '4m ago' },
                { who: 'Ava', what: 'Scored 92/100 (HOT)', t: '4m ago' },
                { who: 'Ava', what: 'Sent welcome SMS', t: '3m ago' },
              ].map((e, i) => (
                <li key={i} className="flex items-center gap-2 text-slate-300">
                  <span className="text-blue-300 w-10 text-[10px] font-medium">{e.who}</span>
                  <span className="flex-1">{e.what}</span>
                  <span className="text-slate-500 text-[10px]">{e.t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </DashShell>
  );
}

/* ─────────────── MESSAGING ─────────────── */
function MessagingMock() {
  return (
    <DashShell active="messaging">
      <h2 className="text-white font-heading text-base font-semibold mb-3">Messaging</h2>
      <div className="grid grid-cols-3 gap-3" style={{ minHeight: 360 }}>
        <div className="col-span-1 bg-white/[0.025] border border-white/[0.06] rounded-xl p-2 space-y-1">
          {[
            { n: 'Sarah Mitchell', last: 'Yes 2pm Saturday works', unread: 0, on: true },
            { n: 'David Park', last: 'Got the wedding pdf, thanks', unread: 1 },
            { n: 'Lisa Nguyen', last: 'I called you back', unread: 0 },
            { n: 'Mike Thompson', last: 'Need to talk to a person', unread: 2 },
          ].map((c, i) => (
            <div key={i} className={`flex items-center gap-2 p-1.5 rounded-md ${c.on ? 'bg-blue-500/15' : ''}`}>
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white text-[10px] font-semibold">{c.n.split(' ').map(x=>x[0]).join('')}</div>
              <div className="min-w-0 flex-1">
                <p className="text-white text-[11px] font-medium truncate">{c.n}</p>
                <p className="text-slate-500 text-[9.5px] truncate">{c.last}</p>
              </div>
              {c.unread > 0 && <span className="text-[9px] bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center font-medium">{c.unread}</span>}
            </div>
          ))}
        </div>
        <div className="col-span-2 bg-white/[0.025] border border-white/[0.06] rounded-xl p-3 flex flex-col">
          <div className="border-b border-white/[0.05] pb-2 mb-2 flex items-center gap-2">
            <p className="text-white text-[12px] font-medium">Sarah Mitchell</p>
            <span className="text-slate-500 text-[10px]">+1 (415) 555-0184</span>
            <span className="ml-auto text-[9.5px] uppercase tracking-wider bg-blue-500/15 text-blue-300 border border-blue-500/30 px-1.5 py-0.5 rounded">Active lead</span>
          </div>
          <div className="flex-1 space-y-2 mb-2 overflow-y-auto">
            <div className="flex items-center gap-1.5 text-[10px] text-slate-500"><Bot className="w-3 h-3" /> Ava sent automated reminder · 1d ago</div>
            <div className="flex justify-end"><div className="bg-blue-600 text-white text-[11px] px-3 py-1.5 rounded-2xl max-w-[75%]">Hi Sarah! Confirming your balayage Saturday at 2pm. Reply C to confirm.</div></div>
            <div className="flex justify-start"><div className="bg-white/[0.06] text-slate-100 text-[11px] px-3 py-1.5 rounded-2xl max-w-[75%]">C</div></div>
            <div className="flex items-center gap-1.5 text-[10px] text-slate-500"><Bot className="w-3 h-3" /> Ava: confirmation logged · just now</div>
            <div className="flex justify-start"><div className="bg-white/[0.06] text-slate-100 text-[11px] px-3 py-1.5 rounded-2xl max-w-[75%]">Quick Q — should I bring anything specific?</div></div>
          </div>
          <div className="bg-white/[0.04] border border-white/10 rounded-full px-3 py-2 text-[11px] text-slate-500 flex items-center gap-2">
            <span>Type your reply… or </span>
            <span className="text-purple-300 inline-flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" /> AI suggest</span>
            <Send className="w-3.5 h-3.5 text-blue-400 ml-auto" />
          </div>
        </div>
      </div>
    </DashShell>
  );
}

/* ─────────────── APPOINTMENTS ─────────────── */
function AppointmentsMock() {
  const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  return (
    <DashShell active="appointments">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white font-heading text-base font-semibold">Appointments</h2>
        <div className="flex items-center gap-1.5 text-[10.5px]">
          <span className="text-slate-300 bg-blue-500/15 border border-blue-500/30 rounded px-2 py-0.5">Week</span>
          <span className="text-slate-400 px-2 py-0.5">Month</span>
          <span className="text-slate-400 px-2 py-0.5">Agenda</span>
        </div>
      </div>
      <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
        <p className="text-white text-[12px] font-medium mb-3">Feb 8 – 14, 2026</p>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d) => <div key={d} className="text-center text-[10px] uppercase tracking-wider text-slate-500 font-medium pb-1.5">{d}</div>)}
          {Array.from({ length: 7 }).map((_, i) => {
            const events = [
              [],
              [{ n: 'Cut · Lisa', t: '10am', tone: 'blue' }],
              [{ n: 'Color · Mike', t: '1pm', tone: 'purple' }],
              [{ n: 'Treatment · Tom', t: '3pm', tone: 'blue' }],
              [{ n: 'Balayage · Sarah', t: '2pm', tone: 'pink' }, { n: 'Trim · Eva', t: '4pm', tone: 'blue' }],
              [{ n: 'Highlights · Ana', t: '11am', tone: 'amber' }, { n: 'Cut · Joe', t: '2pm', tone: 'blue' }],
              [],
            ][i];
            return (
              <div key={i} className="bg-white/[0.02] border border-white/[0.04] rounded-md min-h-[120px] p-1.5 space-y-1">
                {events.map((e, j) => (
                  <div key={j} className={`text-[9px] px-1.5 py-1 rounded leading-tight border ${
                    e.tone==='blue'?'bg-blue-500/15 text-blue-200 border-blue-500/30':
                    e.tone==='purple'?'bg-purple-500/15 text-purple-200 border-purple-500/30':
                    e.tone==='pink'?'bg-pink-500/15 text-pink-200 border-pink-500/30':
                    'bg-amber-500/15 text-amber-200 border-amber-500/30'
                  }`}>
                    <div className="font-semibold">{e.t}</div>
                    <div className="truncate">{e.n}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </DashShell>
  );
}

/* ─────────────── AVA — IDENTITY ─────────────── */
function AvaIdentityMock() {
  return (
    <DashShell active="ava">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center"><Bot className="w-4 h-4 text-white" /></div>
        <h2 className="text-white font-heading text-base font-semibold">Ava</h2>
        <span className="text-[10px] uppercase tracking-wide bg-blue-500/15 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded-full">Live 24/7</span>
        <button className="ml-auto text-[10.5px] font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg px-2.5 py-1 inline-flex items-center gap-1.5"><Phone className="w-3 h-3" /> Test Ava on my phone</button>
      </div>
      <div className="flex items-center gap-3 mb-4 border-b border-white/[0.06] pb-2 text-[12px]">
        <span className="pb-2 -mb-2 border-b-2 border-purple-500 text-purple-300 font-medium">Identity</span>
        <span className="text-slate-400">Knowledge</span>
        <span className="text-slate-400">Playbook</span>
      </div>
      <div className="space-y-2.5">
        <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">AI Name</p>
          <p className="text-white text-[13px]">Ava <span className="text-slate-500 text-[10.5px]">· you can rename to anything (e.g. &quot;Iris&quot;)</span></p>
        </div>
        <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Voice</p>
          <div className="flex gap-1.5">
            {['Avery (warm F)','Sienna (bright F)','Owen (friendly M)','Nadia (calm F)'].map((v,i)=>(
              <span key={i} className={`text-[10.5px] rounded-full px-2.5 py-1 border ${i===0?'bg-blue-500/15 text-blue-300 border-blue-500/30':'bg-white/[0.03] text-slate-400 border-white/10'}`}>{v}</span>
            ))}
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500/[0.07] to-blue-500/[0.07] border border-purple-500/30 rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider text-purple-300 mb-1.5 inline-flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> Locked branded greeting</p>
          <p className="text-[12px] text-slate-100 italic leading-relaxed">&quot;Hello and thank you for calling <strong>Luxe Hair Studio</strong>. This is Ava, powered by NextBell A.I. Please be advised this call may be recorded for quality assurance and training purposes. How may I address your call today?&quot;</p>
          <p className="text-[10.5px] text-slate-500 mt-2">This greeting is locked to ensure brand consistency. The business name auto-fills from your profile.</p>
        </div>
        <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Personality / special instructions</p>
          <p className="text-[12px] text-slate-200 leading-relaxed">Be warm and conversational. Always confirm the customer's preferred stylist if they mention one. Never quote prices over $500 without offering a free consultation.</p>
        </div>
      </div>
    </DashShell>
  );
}

/* ─────────────── AVA — KNOWLEDGE ─────────────── */
function AvaKnowledgeMock() {
  return (
    <DashShell active="ava">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center"><Bot className="w-4 h-4 text-white" /></div>
        <h2 className="text-white font-heading text-base font-semibold">Ava</h2>
        <span className="text-[10px] uppercase tracking-wide bg-blue-500/15 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded-full">Live 24/7</span>
        <button className="ml-auto text-[10.5px] font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg px-2.5 py-1 inline-flex items-center gap-1.5"><Phone className="w-3 h-3" /> Test Ava on my phone</button>
      </div>
      <div className="flex items-center gap-3 mb-4 border-b border-white/[0.06] pb-2 text-[12px]">
        <span className="text-slate-400">Identity</span>
        <span className="pb-2 -mb-2 border-b-2 border-blue-500 text-blue-300 font-medium">Knowledge</span>
        <span className="text-slate-400">Playbook</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <button className="text-[10.5px] bg-blue-500/15 text-blue-300 border border-blue-500/30 rounded-lg px-2 py-2 inline-flex items-center justify-center gap-1.5"><MessageCircle className="w-3 h-3" /> Add Q&amp;A</button>
        <button className="text-[10.5px] bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded-lg px-2 py-2 inline-flex items-center justify-center gap-1.5"><FileText className="w-3 h-3" /> Add note</button>
        <button className="text-[10.5px] bg-red-500/15 text-red-300 border border-red-500/30 rounded-lg px-2 py-2 inline-flex items-center justify-center gap-1.5"><Upload className="w-3 h-3" /> Upload PDF</button>
      </div>
      <div className="space-y-1.5">
        {[
          { kind: 'Q&A', q: 'What are your hours?', a: 'Mon–Sat 9am–7pm. Closed Sundays.', score: 100 },
          { kind: 'Q&A', q: 'How much is balayage?', a: 'Starting at $250 depending on length and condition.', score: 96 },
          { kind: 'PDF', q: 'price_list_2026.pdf', a: '12 chunks indexed · 2 days ago', score: null },
          { kind: 'NOTE', q: 'Cancellation policy', a: '24h notice required to avoid 50% fee.', score: 88 },
          { kind: 'Q&A', q: 'Do you do wedding parties?', a: 'Yes! Group of 4+ get a 15% discount.', score: 92 },
          { kind: 'PDF', q: 'wedding_packages.pdf', a: '8 chunks indexed · just now', score: null, fresh: true },
        ].map((row, i) => (
          <div key={i} className={`bg-white/[0.025] border ${row.fresh ? 'border-blue-500/30' : 'border-white/[0.06]'} rounded-md p-2 flex items-center gap-2.5`}>
            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${row.kind === 'PDF' ? 'bg-red-500/15 text-red-300' : row.kind === 'NOTE' ? 'bg-amber-500/15 text-amber-300' : 'bg-blue-500/15 text-blue-300'}`}>{row.kind}</span>
            <p className="text-white text-[11.5px] flex-1 truncate">{row.q}</p>
            <p className="text-slate-500 text-[10.5px] truncate max-w-[140px]">{row.a}</p>
            {row.score !== null && <span className={`text-[10px] tabular-nums ${row.score >= 90 ? 'text-blue-400' : 'text-amber-400'}`}>{row.score}%</span>}
          </div>
        ))}
      </div>
      <div className="mt-3 bg-blue-500/[0.05] border border-blue-500/20 rounded-lg p-2.5 text-[11px] text-blue-100/90 inline-flex items-center gap-2">
        <Sparkles className="w-3 h-3 text-blue-300" /> Try a question: <span className="bg-white/[0.04] border border-white/10 rounded px-2 py-0.5 ml-1 text-slate-200">&quot;wedding pricing&quot;</span> — Ava finds the right answer in 0.3s.
      </div>
    </DashShell>
  );
}

/* ─────────────── AVA — PLAYBOOK ─────────────── */
function AvaPlaybookMock() {
  return (
    <DashShell active="ava">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center"><Bot className="w-4 h-4 text-white" /></div>
        <h2 className="text-white font-heading text-base font-semibold">Ava</h2>
        <span className="text-[10px] uppercase tracking-wide bg-blue-500/15 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded-full">Live 24/7</span>
        <button className="ml-auto text-[10.5px] font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg px-2.5 py-1 inline-flex items-center gap-1.5"><Phone className="w-3 h-3" /> Test Ava on my phone</button>
      </div>
      <div className="flex items-center gap-3 mb-4 border-b border-white/[0.06] pb-2 text-[12px]">
        <span className="text-slate-400">Identity</span>
        <span className="text-slate-400">Knowledge</span>
        <span className="pb-2 -mb-2 border-b-2 border-blue-500 text-blue-300 font-medium">Playbook</span>
      </div>
      <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10.5px] uppercase tracking-wider text-slate-300 font-semibold">Active during-call playbook</p>
          <span className="text-[10px] uppercase tracking-wider text-blue-300 bg-blue-500/15 border border-blue-500/30 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Power className="w-2.5 h-2.5" /> Active</span>
        </div>
        <p className="text-white text-[12.5px] font-medium mb-1">Master Workflow · v8</p>
        <p className="text-slate-500 text-[10.5px]">Routes calls by intent → quote / book / transfer. Auto-saved 2 minutes ago.</p>
        <button className="mt-2 text-[11px] text-blue-300 inline-flex items-center gap-1"><Edit3 className="w-3 h-3" /> Edit in Flow editor</button>
      </div>
      <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
        <div className="flex items-center justify-between mb-2.5">
          <div>
            <p className="text-[10.5px] uppercase tracking-wider text-slate-300 font-semibold inline-flex items-center gap-1"><History className="w-3 h-3 text-blue-400" /> Playbook versions</p>
            <p className="text-slate-500 text-[10px] mt-0.5">Roll back to a previous version any time. We auto-snapshot before every rollback.</p>
          </div>
          <button className="text-[10.5px] font-medium text-white bg-blue-600 rounded-md px-2.5 py-1 inline-flex items-center gap-1"><Camera className="w-2.5 h-2.5" /> Snapshot now</button>
        </div>
        <ul className="space-y-1.5">
          {[
            { label: 'manual', when: 'Today, 2:14 PM' },
            { label: 'auto', when: 'Today, 11:30 AM' },
            { label: 'pre-rollback', when: 'Yesterday, 6:02 PM' },
            { label: 'auto', when: 'Yesterday, 9:18 AM' },
            { label: 'manual', when: 'Feb 12, 4:45 PM' },
          ].map((v, i) => (
            <li key={i} className="bg-white/[0.03] border border-white/10 rounded-md px-2.5 py-2 flex items-center gap-2 text-[11px]">
              <span className={`text-[9px] uppercase tracking-wider rounded-full px-1.5 py-0.5 ${
                v.label==='pre-rollback'?'bg-amber-500/15 text-amber-300':
                v.label==='manual'?'bg-blue-500/15 text-blue-300':
                'bg-white/[0.06] text-slate-300'
              }`}>{v.label}</span>
              <span className="text-slate-300 flex-1">{v.when}</span>
              <button className="text-blue-300 inline-flex items-center gap-0.5"><RotateCcw className="w-2.5 h-2.5" /> Roll back</button>
            </li>
          ))}
        </ul>
      </div>
    </DashShell>
  );
}

/* ─────────────── AUTOMATIONS ─────────────── */
function FlowTemplatesMock() {
  const T = [
    { name: 'Master Workflow', tag: 'Recommended', d: 'Everything connected on one canvas', icon: Sparkles, color: 'from-purple-500 to-pink-500' },
    { name: 'Missed Call → SMS', tag: 'Popular', d: 'Auto-text every missed call within 2 min', icon: MessageSquare, color: 'from-blue-500 to-cyan-500' },
    { name: 'Reminder Cascade', tag: 'Salons', d: 'Send 24h + 2h reminder SMS before each appt', icon: Clock, color: 'from-blue-500 to-blue-600' },
    { name: 'No-Show Recovery', tag: 'Salons', d: 'Win back no-shows with a discount text', icon: RefreshCw, color: 'from-amber-500 to-orange-500' },
    { name: 'Inbound text → AI callback', tag: 'AI', d: 'Customer texts → AI writes a script → Ava calls them', icon: Wand2, color: 'from-fuchsia-500 to-purple-500' },
    { name: 'Birthday Discount', tag: 'Loyalty', d: 'Auto-send a birthday text with 20% off', icon: Star, color: 'from-pink-500 to-red-500' },
  ];
  return (
    <DashShell active="automations">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white font-heading text-base font-semibold">Automations</h2>
        <div className="flex gap-1.5">
          <button className="text-[10.5px] font-medium text-white bg-gradient-to-r from-fuchsia-600 to-purple-600 rounded-full px-2.5 py-1 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Generate with AI</button>
          <button className="text-[10.5px] font-medium text-white bg-blue-600 rounded-full px-2.5 py-1 inline-flex items-center gap-1"><Plus className="w-3 h-3" /> New flow</button>
        </div>
      </div>
      <p className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Pick a template</p>
      <div className="grid grid-cols-3 gap-2">
        {T.map((t, i) => {
          const Icon = t.icon;
          return (
            <div key={i} className="bg-white/[0.025] border border-white/[0.06] hover:border-white/[0.15] rounded-xl p-3 cursor-pointer">
              <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${t.color} flex items-center justify-center mb-2`}>
                <Icon className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="flex items-center gap-1.5 mb-1">
                <p className="text-white text-[12px] font-medium">{t.name}</p>
                <span className="text-[8.5px] uppercase tracking-wide bg-white/[0.06] text-slate-300 rounded-full px-1.5 py-0.5">{t.tag}</span>
              </div>
              <p className="text-slate-500 text-[10.5px] leading-snug">{t.d}</p>
            </div>
          );
        })}
      </div>
    </DashShell>
  );
}
function FlowCanvasMock() {
  return (
    <DashShell active="automations">
      <div className="bg-[#0d0e10] border border-white/[0.06] rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/[0.05]">
          <ChevronRight className="w-3 h-3 text-slate-500 rotate-180" />
          <span className="text-white text-[12px] font-medium">Master Workflow</span>
          <span className="ml-auto text-[10px] uppercase tracking-wider text-blue-300 bg-blue-500/15 border border-blue-500/30 px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Power className="w-2.5 h-2.5" /> Active</span>
          <span className="text-[10px] text-white bg-purple-600 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><Play className="w-2.5 h-2.5" /> Test fire</span>
          <span className="text-[10px] text-white bg-gradient-to-r from-purple-600 to-blue-600 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" /> AI Review</span>
          <span className="text-[10px] text-white bg-blue-600 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><Save className="w-2.5 h-2.5" /> Save</span>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/[0.05] text-[10px] flex-wrap">
          <span className="text-slate-500 uppercase tracking-wider">ADD:</span>
          {['Stage','Trigger','Action','Agent','Decision','Ava says','Ava asks','Lookup KB'].map(l => (
            <span key={l} className="text-slate-300 bg-white/[0.04] border border-white/10 rounded-full px-2 py-0.5 inline-flex items-center gap-0.5"><Plus className="w-2.5 h-2.5" /> {l}</span>
          ))}
        </div>
        <div className="relative h-[260px] overflow-x-auto overflow-y-hidden" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)', backgroundSize: '14px 14px' }}>
          <div className="absolute inset-0 min-w-[500px]">
            {[
              { x: 8,   y: 96, kind: 'TRIGGER',  l: 'Incoming call',     s: 'Twilio number',  c: 'from-cyan-500 to-blue-500',     icon: Phone },
              { x: 128, y: 96, kind: 'AGENT',    l: 'Ava',                s: 'AI receptionist', c: 'from-purple-500 to-pink-500',  icon: Bot },
              { x: 248, y: 96, kind: 'DECISION', l: 'Caller intent?',     s: 'Branch by intent',c: 'from-amber-500 to-orange-500', icon: GitBranch },
              { x: 368, y: 8,  kind: 'ACTION',   l: 'Quote pricing',      s: 'From Knowledge',  c: 'from-blue-500 to-blue-600', icon: Sparkles },
              { x: 368, y: 96, kind: 'ACTION',   l: 'Book appointment',   s: 'Adds to calendar',c: 'from-blue-500 to-purple-500',  icon: Calendar },
              { x: 368, y: 184,kind: 'ACTION',   l: 'Transfer',           s: 'Hand off to you', c: 'from-rose-500 to-pink-500',    icon: Phone },
            ].map((n, i) => {
              const Icon = n.icon;
              return (
                <div key={i} className="absolute bg-[#1a1a1c] border border-white/[0.08] rounded-lg w-[112px] overflow-hidden shadow-lg" style={{ left: n.x, top: n.y }}>
                  <div className={`bg-gradient-to-r ${n.c} px-1.5 py-0.5 flex items-center gap-1`}>
                    <Icon className="w-2 h-2 text-white" />
                    <span className="text-[7.5px] uppercase tracking-wider text-white font-semibold">{n.kind}</span>
                  </div>
                  <div className="px-1.5 py-1">
                    <p className="text-white text-[9.5px] font-medium leading-tight">{n.l}</p>
                    <p className="text-slate-500 text-[8.5px] mt-0.5 truncate">{n.s}</p>
                  </div>
                </div>
              );
            })}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 500 260" preserveAspectRatio="none">
              <defs><marker id="arrow2" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><polygon points="0 0, 6 3, 0 6" fill="#3b82f6" /></marker></defs>
              {/* Trigger -> Agent */}
              <path d="M120 122 L128 122" stroke="#3b82f6" strokeWidth="1.5" markerEnd="url(#arrow2)" />
              {/* Agent -> Decision */}
              <path d="M240 122 L248 122" stroke="#3b82f6" strokeWidth="1.5" markerEnd="url(#arrow2)" />
              {/* Decision -> Quote (top) */}
              <path d="M360 110 Q364 110 364 34 L368 34" stroke="#3b82f6" strokeWidth="1.5" fill="none" markerEnd="url(#arrow2)" />
              {/* Decision -> Book (middle) */}
              <path d="M360 122 L368 122" stroke="#3b82f6" strokeWidth="1.5" markerEnd="url(#arrow2)" />
              {/* Decision -> Transfer (bottom) */}
              <path d="M360 134 Q364 134 364 210 L368 210" stroke="#3b82f6" strokeWidth="1.5" fill="none" markerEnd="url(#arrow2)" />
            </svg>
          </div>
        </div>
      </div>
    </DashShell>
  );
}
function FlowAIGenMock() {
  return (
    <BrowserChrome url="app.nextbell.ai/dashboard/automations/new">
      <div className="bg-[#0a0a0a] p-8 flex items-center justify-center" style={{ minHeight: 380 }}>
        <div className="bg-[#0d0e10] border border-purple-500/30 rounded-2xl shadow-2xl shadow-purple-500/15 max-w-lg w-full overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.06]">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
            <div>
              <h3 className="text-white font-heading text-base font-semibold">Generate with AI</h3>
              <p className="text-slate-500 text-[11.5px]">Type what you want to happen — we build the flow.</p>
            </div>
          </div>
          <div className="p-5 space-y-3">
            <div className="bg-white/[0.04] border border-purple-500/40 rounded-xl px-3 py-3 text-[12.5px] text-white leading-relaxed">
              When a customer texts asking about wedding packages, send the wedding PDF, then have Ava call them back within 5 minutes with a personalized opener.
            </div>
            <div className="flex flex-wrap gap-1.5">
              {['Friendly tone','Cite KB','Use SMS first','Up to 3 nodes'].map(t=>(
                <span key={t} className="text-[10.5px] bg-white/[0.04] border border-white/10 rounded-full px-2 py-0.5 text-slate-300">{t}</span>
              ))}
            </div>
            <div className="flex items-center justify-between pt-2">
              <span className="text-[11px] text-slate-500">Powered by GPT-5-nano · ~3 sec</span>
              <button className="text-[12px] font-medium text-white bg-gradient-to-r from-fuchsia-600 to-purple-600 rounded-lg px-3.5 py-1.5 inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Generate flow</button>
            </div>
          </div>
        </div>
      </div>
    </BrowserChrome>
  );
}
function FlowReplayMock() {
  return (
    <DashShell active="automations">
      <div className="flex items-center gap-2 mb-3">
        <ChevronRight className="w-3 h-3 text-slate-500 rotate-180" />
        <p className="text-white text-[12px] font-medium">Master Workflow</p>
        <span className="ml-2 text-[10px] uppercase tracking-wider bg-blue-500/15 text-blue-300 border border-blue-500/30 px-2 py-0.5 rounded-full">Run #248</span>
        <span className="ml-auto text-[10px] text-blue-300 inline-flex items-center gap-1"><CircleCheck className="w-2.5 h-2.5" /> Completed in 4.2s</span>
      </div>
      <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-3">Step-by-step replay</p>
        <ol className="space-y-2 relative pl-4 border-l border-white/10 ml-1">
          {[
            { l: 'TRIGGER', n: 'Incoming call from Sarah Mitchell', t: '+0.0s', tone: 'cyan' },
            { l: 'AGENT', n: 'Ava picked up, greeted', t: '+1.2s', tone: 'purple' },
            { l: 'DECISION', n: 'Intent classified: BOOKING', t: '+8.1s', tone: 'amber' },
            { l: 'ACTION', n: 'Booked: balayage Saturday 2pm', t: '+62.4s', tone: 'blue' },
            { l: 'ACTION', n: 'Lead created with score 92', t: '+62.5s', tone: 'emerald' },
            { l: 'ACTION', n: 'Welcome SMS sent', t: '+63.0s', tone: 'emerald' },
          ].map((e, i) => (
            <li key={i} className="relative">
              <span className={`absolute -left-5 top-1 w-3 h-3 rounded-full border-2 bg-[#0a0a0a] ${
                e.tone==='cyan'?'border-cyan-500':e.tone==='purple'?'border-purple-500':e.tone==='amber'?'border-amber-500':e.tone==='blue'?'border-blue-500':'border-blue-500'
              }`} />
              <div className="flex items-center gap-2">
                <span className={`text-[8.5px] uppercase tracking-wider rounded px-1.5 py-0.5 ${
                  e.tone==='cyan'?'bg-cyan-500/15 text-cyan-300':e.tone==='purple'?'bg-purple-500/15 text-purple-300':e.tone==='amber'?'bg-amber-500/15 text-amber-300':e.tone==='blue'?'bg-blue-500/15 text-blue-300':'bg-blue-500/15 text-blue-300'
                }`}>{e.l}</span>
                <span className="text-[11.5px] text-slate-200 flex-1">{e.n}</span>
                <span className="text-[10px] text-slate-500 tabular-nums">{e.t}</span>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </DashShell>
  );
}

/* ─────────────── ANALYTICS — 3 mocks ─────────────── */
function AnalyticsKPIsMock() {
  return (
    <DashShell active="analytics">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white font-heading text-base font-semibold">Analytics</h2>
        <div className="flex gap-1.5 text-[10.5px]">
          <span className="bg-blue-500/15 text-blue-300 border border-blue-500/30 rounded-full px-2.5 py-1">Last 30 days</span>
          <span className="text-slate-400 px-2.5 py-1">90 days</span>
          <span className="text-slate-400 px-2.5 py-1">All time</span>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2.5">
        {[
          { l: 'Total calls', v: '342', d: '+12%' },
          { l: 'Answered', v: '328', sub: '95.9%', d: '+2pp' },
          { l: 'Booked', v: '187', sub: '54.7%', d: '+9pp' },
          { l: 'Revenue', v: '$24,842', d: '+18%' },
        ].map((k) => (
          <div key={k.l} className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
            <p className="text-[9.5px] uppercase tracking-wider text-slate-500 font-medium">{k.l}</p>
            <p className="text-white font-heading text-2xl font-medium tabular-nums mt-0.5">{k.v}</p>
            {k.sub && <p className="text-slate-300 text-[10.5px]">{k.sub}</p>}
            <p className="text-[10px] text-blue-400 mt-0.5">{k.d}</p>
          </div>
        ))}
      </div>
      <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3 mt-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-3">Calls per day · last 14 days</p>
        <div className="flex items-end gap-1.5 h-[120px]">
          {[18,22,14,28,32,38,24,19,26,30,41,35,28,23].map((h,i)=>(
            <div key={i} className="flex-1 bg-gradient-to-t from-blue-500/40 to-blue-500/80 rounded-t" style={{ height: `${(h/41)*100}%` }} />
          ))}
        </div>
        <div className="flex justify-between mt-1.5 text-[9px] text-slate-500">
          <span>Feb 1</span><span>Feb 14</span>
        </div>
      </div>
    </DashShell>
  );
}
function AnalyticsSourcesMock() {
  const sources = [
    { n: 'Inbound calls', v: 142, pct: 42, c: 'bg-blue-500' },
    { n: 'Web form', v: 89, pct: 26, c: 'bg-purple-500' },
    { n: 'Inbound SMS', v: 64, pct: 19, c: 'bg-sky-500' },
    { n: 'Referrals', v: 47, pct: 13, c: 'bg-pink-500' },
  ];
  return (
    <DashShell active="analytics">
      <h2 className="text-white font-heading text-base font-semibold mb-3">Lead sources & top services</h2>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-3">Where leads come from</p>
          <div className="space-y-2.5">
            {sources.map((s,i)=>(
              <div key={i}>
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <span className="text-white">{s.n}</span>
                  <span className="text-slate-400 tabular-nums">{s.v} <span className="text-slate-500">({s.pct}%)</span></span>
                </div>
                <div className="h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
                  <div className={`h-full ${s.c}`} style={{ width: `${s.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-3">Top services this month</p>
          <div className="space-y-2">
            {[
              { n: 'Balayage', cnt: 47, rev: '$11,750' },
              { n: 'Color', cnt: 62, rev: '$7,440' },
              { n: 'Highlights', cnt: 38, rev: '$6,840' },
              { n: 'Cut & style', cnt: 84, rev: '$5,040' },
              { n: 'Treatment', cnt: 29, rev: '$2,175' },
            ].map((s,i)=>(
              <div key={i} className="flex items-center justify-between text-[11px] bg-white/[0.025] rounded-lg px-2.5 py-2">
                <span className="text-white">{s.n}</span>
                <span className="text-slate-400 tabular-nums">{s.cnt}</span>
                <span className="text-blue-400 tabular-nums">{s.rev}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashShell>
  );
}
function AnalyticsHeatmapMock() {
  const hours = ['8a','9a','10a','11a','12p','1p','2p','3p','4p','5p','6p','7p'];
  const days = ['Mon','Tue','Wed','Thu','Fri','Sat'];
  const data = [
    [1,2,4,5,4,3,2,3,4,5,4,2],
    [1,3,4,6,5,4,3,4,5,6,5,3],
    [1,2,3,5,4,3,2,3,4,5,4,2],
    [2,3,5,6,5,4,3,4,5,6,5,3],
    [3,4,6,8,7,5,4,5,6,7,6,4],
    [5,7,9,9,7,6,5,6,7,8,6,3],
  ];
  const cell = (v) => v >= 7 ? 'bg-blue-500/80' : v >= 5 ? 'bg-blue-500/55' : v >= 3 ? 'bg-blue-500/30' : 'bg-blue-500/10';
  return (
    <DashShell active="analytics">
      <h2 className="text-white font-heading text-base font-semibold mb-3">Peak-hours heatmap</h2>
      <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-3">Calls by day-of-week + hour</p>
        <div className="overflow-x-auto">
          <table className="text-[9px] min-w-[420px] border-separate border-spacing-1">
            <thead>
              <tr>
                <th></th>
                {hours.map(h => <th key={h} className="text-slate-500 font-normal text-center w-[22px]">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {days.map((d, i) => (
                <tr key={d}>
                  <td className="text-slate-500 pr-1">{d}</td>
                  {data[i].map((v, j) => (
                    <td key={j} className={`w-[22px] h-[22px] rounded ${cell(v)} text-white text-center text-[8.5px] tabular-nums align-middle`}>{v > 0 ? v : ''}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[10.5px] text-blue-300 mt-3 inline-flex items-center gap-1"><Sparkles className="w-2.5 h-2.5" /> Peak: Saturday 10–11 AM. Consider opening earlier.</p>
      </div>
    </DashShell>
  );
}

/* ─────────────── SETTINGS — 4 mocks ─────────────── */
function SettingsTabBar({ active }) {
  return (
    <div className="flex gap-1.5 mb-4 text-[11.5px] flex-wrap">
      {['Profile','Business','Billing','API'].map((t) => (
        <span key={t} className={`px-3 py-1.5 rounded-full ${t.toLowerCase() === active ? 'bg-blue-500/15 text-blue-300 border border-blue-500/30' : 'text-slate-400 border border-white/[0.06]'}`}>{t}</span>
      ))}
    </div>
  );
}
function SettingsProfileMock() {
  return (
    <DashShell active="settings">
      <h2 className="text-white font-heading text-base font-semibold mb-3">Settings</h2>
      <SettingsTabBar active="profile" />
      <div className="space-y-3">
        <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3 grid grid-cols-2 gap-2.5">
          <Field label="Your name" value="Alex Rivera" />
          <Field label="Email" value="alex@luxehair.com" />
          <Field label="Password" value="••••••••••••" />
          <Field label="Phone" value="+1 (415) 555-0184" />
        </div>
        <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-white text-[12.5px] font-medium inline-flex items-center gap-1.5"><Shield className="w-3 h-3 text-blue-400" /> Two-factor authentication</p>
              <p className="text-slate-500 text-[10.5px] mt-0.5">Email-based 6-digit OTP on every login.</p>
            </div>
            <span className="w-9 h-5 rounded-full bg-blue-500/40 relative"><span className="absolute top-0.5 left-4 w-4 h-4 rounded-full bg-white" /></span>
          </div>
        </div>
        <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
          <p className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Active sessions</p>
          <ul className="space-y-1.5 text-[11px]">
            {[
              { d: 'MacBook Pro · San Francisco', t: 'Active now', cur: true },
              { d: 'iPhone · San Francisco', t: '2 hours ago' },
              { d: 'Chrome on Windows · LA', t: 'Yesterday' },
            ].map((s, i) => (
              <li key={i} className="flex items-center gap-2 bg-white/[0.03] border border-white/10 rounded-md px-2.5 py-2">
                <Smartphone className="w-3 h-3 text-slate-400" />
                <span className="text-slate-200 flex-1">{s.d}</span>
                {s.cur ? <span className="text-[10px] text-blue-300">{s.t}</span> : <span className="text-[10px] text-slate-500">{s.t}</span>}
                {!s.cur && <span className="text-[10px] text-red-400">Sign out</span>}
              </li>
            ))}
          </ul>
          <button className="text-[11px] text-red-400 mt-2 inline-flex items-center gap-1"><LogOut className="w-3 h-3" /> Sign out everywhere else</button>
        </div>
      </div>
    </DashShell>
  );
}
function SettingsBusinessMock() {
  const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  return (
    <DashShell active="settings">
      <h2 className="text-white font-heading text-base font-semibold mb-3">Settings</h2>
      <SettingsTabBar active="business" />
      <div className="space-y-3">
        <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3 grid grid-cols-2 gap-2.5">
          <Field label="Business name" value="Luxe Hair Studio" />
          <Field label="Industry" value="Salon / Spa" />
          <Field label="Business phone" value="+1 (415) 555-0184" />
          <Field label="Address" value="123 Main St, San Francisco" icon={MapPin} />
        </div>
        <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
          <p className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Business hours</p>
          <div className="grid grid-cols-7 gap-1.5">
            {days.map((d,i)=>(
              <div key={d} className={`text-center text-[10.5px] rounded-md p-1.5 ${i===6?'bg-white/[0.02] text-slate-600':'bg-blue-500/10 text-blue-200'}`}>
                <div className="font-semibold">{d}</div>
                <div className="text-[9px] mt-0.5">{i===6?'Closed':'9am – 7pm'}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Services</p>
            <button className="text-[10.5px] text-blue-300 inline-flex items-center gap-0.5"><Plus className="w-2.5 h-2.5" /> Add service</button>
          </div>
          <div className="space-y-1.5">
            {[
              { n: 'Cut & style', p: 60, t: 45 },
              { n: 'Color', p: 120, t: 90 },
              { n: 'Balayage', p: 250, t: 180 },
              { n: 'Highlights', p: 180, t: 120 },
              { n: 'Treatment', p: 75, t: 60 },
              { n: 'Wedding package (group of 4+)', p: 950, t: 240 },
            ].map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center text-[11px]">
                <span className="col-span-6 text-white">{s.n}</span>
                <span className="col-span-2 text-slate-300 text-right tabular-nums">${s.p}</span>
                <span className="col-span-2 text-slate-300 text-right tabular-nums">{s.t} min</span>
                <Edit3 className="col-span-1 w-3 h-3 text-slate-500 mx-auto" />
                <Trash2 className="col-span-1 w-3 h-3 text-slate-600 mx-auto" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashShell>
  );
}
function SettingsBillingMock() {
  return (
    <DashShell active="settings">
      <h2 className="text-white font-heading text-base font-semibold mb-3">Settings</h2>
      <SettingsTabBar active="billing" />
      <div className="space-y-3">
        <div className="bg-gradient-to-br from-blue-500/[0.07] to-purple-500/[0.07] border border-blue-500/20 rounded-xl p-4 flex items-center gap-3">
          <div>
            <p className="text-[10.5px] uppercase tracking-wider text-blue-300 font-semibold mb-1">Current plan</p>
            <p className="text-white font-heading text-xl font-medium">Growth · $747/mo</p>
            <p className="text-slate-400 text-[11.5px] mt-0.5">Renews March 14, 2026 · 7-day free trial active until Feb 17</p>
          </div>
          <div className="ml-auto flex flex-col gap-1.5">
            <button className="text-[11px] bg-white/[0.04] border border-white/10 rounded-md px-3 py-1.5 text-slate-300">Change plan</button>
            <button className="text-[11px] bg-red-500/15 border border-red-500/30 rounded-md px-3 py-1.5 text-red-300">Cancel</button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2.5">
          {[
            { l: 'Voice minutes', cur: 847, max: 1200 },
            { l: 'SMS', cur: 312, max: 500 },
            { l: 'Knowledge entries', cur: 47, max: 'Unlimited' },
          ].map((u,i) => (
            <div key={i} className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
              <p className="text-[9.5px] uppercase tracking-wider text-slate-500 font-medium mb-1.5">{u.l}</p>
              <p className="text-white font-heading text-base tabular-nums">{u.cur} <span className="text-slate-500 text-[11px]">/ {u.max}</span></p>
              {typeof u.max === 'number' && (
                <div className="h-1 bg-white/[0.05] rounded-full overflow-hidden mt-2">
                  <div className="h-full bg-blue-500" style={{ width: `${(u.cur/u.max)*100}%` }} />
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
          <p className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Payment method</p>
          <div className="flex items-center gap-2.5 bg-white/[0.03] border border-white/10 rounded-lg p-2.5">
            <div className="w-9 h-6 rounded bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center"><CreditCard className="w-3 h-3 text-slate-300" /></div>
            <div>
              <p className="text-white text-[12px] font-medium">Visa •••• 4242</p>
              <p className="text-slate-500 text-[10.5px]">Expires 12/27</p>
            </div>
            <button className="ml-auto text-[10.5px] text-blue-300">Change</button>
          </div>
        </div>
        <div className="bg-blue-500/[0.05] border border-blue-500/20 rounded-xl p-3 text-[11.5px] text-blue-100/90 inline-flex items-center gap-2 w-full">
          <Shield className="w-3.5 h-3.5 text-blue-300 flex-shrink-0" />
          <span>You qualify for a self-serve refund — within 7 days of payment and Ava hasn&apos;t taken any calls on this cycle. <span className="text-blue-300 underline">Request refund</span></span>
        </div>
      </div>
    </DashShell>
  );
}
function SettingsApiMock() {
  return (
    <DashShell active="settings">
      <h2 className="text-white font-heading text-base font-semibold mb-3">Settings</h2>
      <SettingsTabBar active="api" />
      <div className="space-y-3">
        <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10.5px] uppercase tracking-wider text-slate-300 font-semibold inline-flex items-center gap-1.5"><Key className="w-3 h-3 text-amber-400" /> Personal API key</p>
            <button className="text-[10.5px] font-medium text-white bg-blue-600 rounded-md px-2.5 py-1">Generate key</button>
          </div>
          <div className="bg-[#0d0e10] border border-white/10 rounded-lg p-2.5 font-mono text-[11px] text-slate-300 truncate">nbk_live_•••••••••••••••••••••••••••••• <span className="text-blue-300 ml-2">Copy</span></div>
          <p className="text-slate-500 text-[10.5px] mt-2">Last used 2 days ago from 35.247.x.x</p>
        </div>
        <div className="bg-white/[0.025] border border-white/[0.06] rounded-xl p-3">
          <p className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Webhooks</p>
          <ul className="space-y-1.5">
            {[
              { url: 'https://hooks.luxehair.com/calls', events: ['call.completed','call.started'], on: true },
              { url: 'https://make.com/wh/abc123', events: ['lead.created'], on: true },
              { url: 'https://zapier.com/hooks/xyz', events: ['appointment.booked'], on: false },
            ].map((w, i) => (
              <li key={i} className="bg-white/[0.03] border border-white/10 rounded-md p-2 flex items-center gap-2 text-[11px]">
                <span className={`w-2 h-2 rounded-full ${w.on ? 'bg-blue-400' : 'bg-slate-500'} flex-shrink-0`} />
                <span className="text-slate-200 font-mono text-[10.5px] truncate flex-1">{w.url}</span>
                <span className="text-slate-500 text-[10px]">{w.events.length} event{w.events.length>1?'s':''}</span>
              </li>
            ))}
          </ul>
          <button className="mt-2 text-[11px] text-blue-300 inline-flex items-center gap-1"><Plus className="w-2.5 h-2.5" /> Add webhook</button>
        </div>
      </div>
    </DashShell>
  );
}

/* ─────────────── TEST CALL ─────────────── */
function TestCallMock() {
  return (
    <BrowserChrome url="app.nextbell.ai/dashboard/ava">
      <div className="bg-[#0a0a0a] p-8 flex items-center justify-center" style={{ minHeight: 360 }}>
        <div className="bg-[#0d0e10] border border-blue-500/30 rounded-2xl shadow-2xl shadow-blue-500/15 max-w-md w-full overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.06]">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center"><Phone className="w-4 h-4 text-white" /></div>
            <div>
              <h3 className="text-white font-heading text-base font-semibold">Test Ava live</h3>
              <p className="text-slate-500 text-[11.5px]">We&apos;ll call your phone in 5 seconds.</p>
            </div>
          </div>
          <div className="p-5 space-y-3">
            <Field label="Your phone (E.164)" value="+1 415 555 0184" focused />
            <p className="text-[11px] text-slate-500">Tip: ask Ava 3 things — your hours, a service price, and &quot;I&apos;d like to book&quot;.</p>
          </div>
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-white/[0.06] bg-white/[0.02]">
            <span className="text-[12px] text-slate-300 px-3 py-1.5">Cancel</span>
            <span className="text-[12px] font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg px-3.5 py-1.5 inline-flex items-center gap-1.5"><Phone className="w-3 h-3" /> Call me</span>
          </div>
        </div>
      </div>
    </BrowserChrome>
  );
}

/* ─────────────── HOW IT ALL CONNECTS ─────────────── */
function HowItConnectsDiagram() {
  const Box = ({ icon: Icon, title, sub, color }) => (
    <div className="bg-[#101113] border border-white/[0.08] rounded-xl overflow-hidden">
      <div className={`bg-gradient-to-r ${color} px-3 py-1.5 flex items-center gap-1.5`}>
        <Icon className="w-3 h-3 text-white" />
        <span className="text-[10.5px] uppercase tracking-wider text-white font-semibold">{title}</span>
      </div>
      <div className="p-3"><p className="text-slate-400 text-[11.5px] leading-snug">{sub}</p></div>
    </div>
  );
  const Arrow = () => (
    <div className="flex items-center justify-center text-blue-400">
      <ArrowRight className="w-5 h-5" />
    </div>
  );
  const phase = (label, color) => (
    <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold mb-3 ${color}`}>{label}</div>
  );
  return (
    <div className="space-y-6">
      {/* Phase 1: customer reaches out */}
      <div>
        {phase('Phase 1 · Customer reaches out', 'text-cyan-300')}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 items-stretch">
          <Box icon={Phone} title="Calls" color="from-cyan-500 to-blue-500" sub="Sarah dials your business. Ava picks up within one ring." />
          <Arrow />
          <Box icon={Bot} title="Ava brain" color="from-purple-500 to-pink-500" sub="Identity + Knowledge + Playbook decide every word she says." />
          <Arrow />
          <Box icon={BookOpen} title="Knowledge" color="from-blue-500 to-blue-600" sub="Ava searches your KB to quote prices, hours, policies." />
        </div>
      </div>

      {/* Phase 2: capture & decide */}
      <div>
        {phase('Phase 2 · Capture and decide', 'text-pink-300')}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 items-stretch">
          <Box icon={Users} title="Leads" color="from-pink-500 to-rose-500" sub="Caller becomes a scored lead. Ava scores 0–100 by intent + urgency." />
          <Arrow />
          <Box icon={Workflow} title="Automations" color="from-amber-500 to-orange-500" sub="Triggers fire when events happen. Each one runs a flow you designed." />
          <Arrow />
          <Box icon={Calendar} title="Appointments" color="from-blue-500 to-indigo-500" sub="Ava books straight into your calendar. Two-way sync, no double-booking." />
        </div>
      </div>

      {/* Phase 3: follow-up */}
      <div>
        {phase('Phase 3 · Follow-up and analytics', 'text-blue-300')}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
          <Box icon={MessageSquare} title="Messaging" color="from-blue-500 to-cyan-500" sub="Confirmations, reminders, win-back texts auto-fire from your flows." />
          <Arrow />
          <Box icon={BarChart3} title="Analytics" color="from-indigo-500 to-blue-500" sub="Every action above feeds the dashboard charts in real time." />
        </div>
      </div>

      <p className="text-[12.5px] text-slate-400 leading-relaxed max-w-3xl">
        One customer, one journey. Sarah calls → Ava classifies the intent using your <strong className="text-white">Knowledge Base</strong> + <strong className="text-white">Playbook</strong> → her info becomes a scored <strong className="text-white">Lead</strong> → she gets booked into <strong className="text-white">Appointments</strong> → an <strong className="text-white">SMS</strong> confirmation goes out → and every step shows up in your <strong className="text-white">Analytics</strong>. No spreadsheets, no copy-pasting, no manual hand-offs.
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   PAGE
   ───────────────────────────────────────────────────────────────── */
export default function Ad3Page() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#070707] text-white">

      {/* HERO */}
      <section className="relative overflow-hidden pt-20 pb-16 sm:pt-28 sm:pb-24 px-5 sm:px-8">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full bg-blue-500/10 blur-[120px]" />
          <div className="absolute -top-20 right-0 w-[420px] h-[420px] rounded-full bg-purple-500/10 blur-[100px]" />
        </div>
        <div className="relative max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-3 py-1 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-slate-300 font-medium">Customer training · v3</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight max-w-4xl mx-auto">
            Zero to live.<br />
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">Every screen. Every tap. Every setting.</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-slate-400 text-base sm:text-lg leading-relaxed">
            The complete walkthrough. By the end of this, your AI receptionist Ava is taking calls, booking real customers, texting confirmations, and feeding everything into one dashboard you actually understand.
          </p>
          <div className="flex items-center justify-center flex-wrap gap-3 mt-8 text-[12px] text-slate-500">
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> 25-minute read</span>
            <span className="w-1 h-1 rounded-full bg-slate-700" />
            <span className="flex items-center gap-1.5"><Wifi className="w-3.5 h-3.5" /> No coding required</span>
            <span className="w-1 h-1 rounded-full bg-slate-700" />
            <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> 7-day free trial</span>
          </div>
        </div>
      </section>

      {/* TOC */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-10">
        <GlassPanel className="p-5 sm:p-7">
          <p className="text-[11px] uppercase tracking-wider text-blue-400 font-semibold mb-4">What you&apos;ll learn</p>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
            {[
              '01 · Sign-up wizard, page by page',
              '02 · Sign in (with optional 2FA)',
              '03 · First-run onboarding (5 questions)',
              '04 · Dashboard tour and home screen',
              '05 · Calls — list, filters, replay',
              '06 · Leads — pipeline + lead detail',
              '07 · Messaging — 2-way SMS',
              '08 · Appointments — week & day calendar',
              '09 · Ava → Identity tab',
              '10 · Ava → Knowledge Base',
              '11 · Ava → Playbook + versions',
              '12 · Automations → templates',
              '13 · Automations → flow editor',
              '14 · Automations → AI generator',
              '15 · Automations → run replay',
              '16 · Analytics → KPIs + chart',
              '17 · Analytics → sources + services',
              '18 · Analytics → peak hours heatmap',
              '19 · Settings → profile + 2FA + sessions',
              '20 · Settings → business + hours + services',
              '21 · Settings → billing + usage + refund',
              '22 · Settings → API + webhooks',
              '23 · Test Ava on your phone',
              '24 · How everything connects',
              '25 · Going-live checklist',
            ].map((line) => (
              <div key={line} className="flex items-center gap-2 text-[13px] text-slate-300">
                <ChevronRight className="w-3 h-3 text-slate-600" /> {line}
              </div>
            ))}
          </div>
        </GlassPanel>
      </section>

      {/* ─── 1. SIGN-UP WIZARD (5 pages) ─── */}
      <Section
        n={1} sub="Page 1 of 5"
        kicker="Sign up"
        title="Page 1 — Create your account"
        lede="The first page of signup. Three fields, one checkbox, one button. We never ask for a credit card up front."
        mock={<SignupStep1Mock />}
        instructions={[
          <>From the homepage, click <strong className="text-white">Start free trial</strong> (top-right, or any pricing card).</>,
          <>Type your full name. This is what shows on receipts and welcome emails.</>,
          <>Type your email — we send a verification + welcome message after signup.</>,
          <>Pick a password (minimum 8 characters, must include 1 number).</>,
          <>Tick the box next to &quot;I agree to the Terms and Privacy Policy&quot;.</>,
          <>Tap <strong className="text-white">Continue</strong> to advance to Page 2.</>,
        ]}
        why="Your email is your username forever. Pick the one you actually check. Your password is hashed before it leaves your browser, we cannot ever see it, even if we wanted to."
        tip="Lost the password later? No problem — the Login page has a Forgot password link that emails you a one-shot reset link valid for 30 minutes."
      />
      <Section
        sub="Page 2 of 5" kicker="Sign up"
        title="Page 2 — Tell us about your business"
        lede="Four fields. Ava uses every one of them: your business name shapes her greeting, your industry preselects her starter Knowledge Base, and your website is scraped to teach her your tone and value props."
        mock={<SignupStep2Mock />}
        instructions={[
          <>Type the public-facing name of your business. This appears in the locked greeting on every call.</>,
          <>Pick your industry from the dropdown. The full list available today: <strong className="text-white">Salon &amp; Spa</strong>, <strong className="text-white">Hair Studio</strong>, <strong className="text-white">Barbershop</strong>, <strong className="text-white">Medical Practice</strong>, <strong className="text-white">Dental Practice</strong>, <strong className="text-white">Veterinary Clinic</strong>, <strong className="text-white">Real Estate</strong>, <strong className="text-white">Law Firm</strong>, <strong className="text-white">Contractor / Home Services</strong> (HVAC, plumbing, electrical, landscaping), <strong className="text-white">Restaurant</strong>, <strong className="text-white">Hotel / Hospitality</strong>, <strong className="text-white">Fitness Studio / Gym</strong>, <strong className="text-white">Photography</strong>, <strong className="text-white">Currency Exchange / FX</strong> (FINTRAC compliant), <strong className="text-white">Accounting / Bookkeeping</strong>, <strong className="text-white">Insurance Brokerage</strong>, <strong className="text-white">Auto Repair / Detailing</strong>, <strong className="text-white">Cleaning Services</strong>, <strong className="text-white">Tutoring / Education</strong>, <strong className="text-white">Coaching / Consulting</strong>, <strong className="text-white">Retail Storefront</strong>, and <strong className="text-white">Other</strong> (custom — Ava adapts to whatever you describe).</>,
          <>Enter your business phone number. This is the line customers currently dial. Ava will sit on top of it once you forward calls.</>,
          <>(Optional but powerful) Paste your website URL. Behind the scenes Ava scrapes it, learns your tone, services, FAQs, and bakes them into her brain.</>,
          <>Tap <strong className="text-white">Continue</strong>.</>,
        ]}
        connects={<>Your business name appears on every <strong>Call</strong> and every <strong>SMS</strong>. Changing it later in <strong>Settings → Business</strong> updates everywhere automatically.</>}
        tip="Pick Salon and we pre-fill typical hours, services, and intake questions. Pick Currency Exchange and we ask for your FINTRAC MSB number instead, the wizard adapts to your industry."
      />
      <Section
        sub="Page 3 of 5" kicker="Sign up"
        title="Page 3 — How does Ava sound?"
        lede="Greeting tone, languages, and one signature FAQ. This is where your AI starts feeling like YOUR assistant, not a generic robot."
        mock={<SignupStep3Mock />}
        instructions={[
          <>Pick a greeting style. Friendly+warm for most service businesses, Luxury for high-end salons or hotels, Professional for clinics or law firms.</>,
          <>Pick all languages your customers might speak. Ava auto-detects the caller&apos;s language and responds in it.</>,
          <>Type one signature FAQ — the question you get asked the most. Anything from &quot;Do you offer parking?&quot; to &quot;What&apos;s your cancellation policy?&quot;.</>,
          <>Tap <strong className="text-white">Continue</strong>.</>,
        ]}
        why="Your greeting tone is the first 5 seconds of every customer call. Get it right and customers feel they&apos;ve reached the right place. Get it wrong and they hang up."
      />
      <Section
        sub="Page 4 of 5" kicker="Sign up"
        title="Page 4 — Pick your plan"
        lede="Three tiers. Switch any time. The trial is 7 days, completely free, no card on file."
        mock={<SignupStep4Mock />}
        instructions={[
          <><strong className="text-white">Starter $297/mo</strong> — 400 voice minutes, 100 SMS, 1 location, 1 Ava number. Lead capture, appointment booking, basic dashboard. Ideal for solo operators.</>,
          <><strong className="text-white">Growth $747/mo</strong> — 1,200 minutes, 500 SMS, everything in Starter PLUS appointment reminders, automatic follow-up SMS, automatic re-engagement campaigns, full niche-specific dashboard, analytics, priority support. Best fit for most growing businesses.</>,
          <><strong className="text-white">Agency $1997/mo</strong> — 3,500 minutes, 2,000 SMS, up to 5 locations &amp; Ava numbers, Ava handles everything end to end, white-label option, multi-user team access, priority phone support. Need more than 5 locations? Upgrade to Enterprise.</>,
          <><strong className="text-white">Enterprise — custom</strong> — unlimited locations, custom voice &amp; persona, custom integrations, SLA guarantee, dedicated account manager, 24/7 priority support. Tap to talk to our team.</>,
          <>Toggle <strong className="text-white">Annual billing</strong> at the top to save ~17% (about 2 months free) on Starter, Growth and Agency.</>,
          <>Tap a plan to select it. Tap <strong className="text-white">Continue</strong>.</>,
        ]}
        why="The 7-day free trial is genuinely free, no card on file. Test on real customers, downgrade, upgrade, or walk away — only enter card details when you're ready to keep going past day 7."
      />
      <Section
        sub="Page 5 of 5" kicker="Sign up"
        title="Page 5 — Activate"
        lede="Last screen. We show you a summary, including your brand-new dedicated NextBell phone number that&apos;s being provisioned in real time."
        mock={<SignupStep5Mock />}
        instructions={[
          <>Review every line. If anything looks wrong, tap <strong className="text-white">Back</strong> to fix it.</>,
          <>Notice your <strong className="text-blue-300">NextBell phone number</strong> — that&apos;s your AI receptionist&apos;s number, ready to receive calls right now.</>,
          <>Tap <strong className="text-white">Activate my account</strong>. The system creates your Twilio number, provisions Ava with default Knowledge, and signs you in automatically.</>,
        ]}
        connects={<>The phone number you see is permanently yours. You can either give it directly to customers, or set up call-forwarding from your existing line so customers always dial what they remember.</>}
      />

      {/* ─── 2. SIGN IN ─── */}
      <Section
        n={2} kicker="Auth"
        title="Sign in any time"
        lede="The /login screen is two fields, a human-check, and a Continue button. Forgot-password is right there. If you&apos;ve enabled 2FA, you get a 6-digit email code as a second step."
        mock={<LoginMock />}
        instructions={[
          <>Type your email and password.</>,
          <>Tick the &quot;I am human&quot; box. (No CAPTCHA, just a quick interaction-check.)</>,
          <>Tap <strong className="text-white">Continue</strong>. If 2FA is off, you&apos;re straight in.</>,
          <>If 2FA is on (recommended), the next page asks for the 6-digit code we just emailed you.</>,
          <>Sessions auto-expire after 15 minutes of inactivity for safety.</>,
        ]}
        why="Your dashboard touches real customer data, real bookings, real money. A 2-step login is the bare minimum for that level of trust, you&apos;ll see banks, doctors, payroll all do the same."
        tip="Forgot password? Tap the link, give us your email, we send a one-shot reset link that expires in 30 minutes. The old password keeps working until you set a new one — so you can&apos;t lock yourself out."
      />
      <Section
        sub="If 2FA is on" kicker="Auth"
        title="The 6-digit code"
        lede="If you&apos;ve flipped on Two-factor authentication in Settings → Profile, every sign-in adds this step. Codes are valid for 10 minutes and one-time-use only."
        mock={<TwoFAMock />}
        instructions={[
          <>Open the email we just sent you (subject: <em className="text-slate-300">&quot;Your NextBell verification code&quot;</em>).</>,
          <>Type the 6 digits. We auto-advance the cursor between boxes.</>,
          <>Tap <strong className="text-white">Verify and sign in</strong>.</>,
          <>Didn&apos;t get it? Tap <strong className="text-white">Resend</strong> — we generate a fresh code and invalidate the old one.</>,
        ]}
        why="Even if a bad actor steals your password somehow, they can&apos;t sign in without ALSO controlling your email account. 2FA adds a second lock so a single leak isn&apos;t catastrophic."
      />

      {/* ─── 3. ONBOARDING — 5 sub-mocks ─── */}
      <Section
        n={3} sub="Onboarding 1 of 5" kicker="First-time setup"
        title="Pick a greeting tone"
        lede="The very first time you sign in, a 5-question wizard pops up over the dashboard. You can&apos;t skip it (Ava needs the answers), but each step takes &lt; 30 seconds."
        mock={<Ob1Greeting />}
        instructions={[
          <>Tap the tile that matches your brand best. Most service businesses pick <strong className="text-white">Friendly + warm</strong>.</>,
          <>The selection is highlighted blue. Tap <strong className="text-white">Continue</strong>.</>,
          <>You can change tone any time later in <strong>Ava → Identity</strong>.</>,
        ]}
        connects={<>The tone you pick here is loaded into Ava&apos;s system prompt. Every word she says — even with the best Knowledge Base on Earth — is filtered through this style.</>}
      />
      <Section
        sub="Onboarding 2 of 5" kicker="First-time setup"
        title="Pick languages"
        lede="Ava is multilingual out of the box. She auto-detects what the caller is speaking and responds natively. Pick every language you might encounter."
        mock={<Ob2Languages />}
        instructions={[
          <>Tap each language pill to toggle it on. English is on by default.</>,
          <>For best results, pick at least 2 if you&apos;re in a multilingual market.</>,
          <>Add more later in <strong>Ava → Identity → Languages</strong>.</>,
        ]}
      />
      <Section
        sub="Onboarding 3 of 5" kicker="First-time setup"
        title="Add your services & pricing"
        lede="Every service you charge for, with a price and duration. Ava quotes from this list and uses durations to fit appointments correctly into your calendar."
        mock={<Ob3Services />}
        instructions={[
          <>Tap <strong className="text-white">+ Add service</strong> to add a row.</>,
          <>Type the service name (e.g. &quot;Balayage&quot;).</>,
          <>Type the starting price (you can use ranges: &quot;250-400&quot; if needed).</>,
          <>Type the duration in minutes — used by Ava and the calendar to avoid double-bookings.</>,
          <>Trash icon removes a row.</>,
        ]}
        why="If a service is missing here, Ava will say &quot;Let me check on that, I&apos;ll have someone call you back&quot; instead of guessing. So fill in everything you do — even rare services."
      />
      <Section
        sub="Onboarding 4 of 5" kicker="First-time setup"
        title="Set your business hours"
        lede="Per-day toggles + open/close times. Ava only books inside these hours, even if a caller insists on a Sunday."
        mock={<Ob4Hours />}
        instructions={[
          <>Each day has a toggle. Off = closed.</>,
          <>For open days, tap the time range to edit start/end.</>,
          <>Holidays and one-off closures live in <strong>Settings → Business</strong> later.</>,
        ]}
        connects={<>This affects: Ava&apos;s booking logic, the Appointments calendar greys out closed slots, and SMS reminders use these hours when scheduling 24-hour-before nudges.</>}
      />
      <Section
        sub="Onboarding 5 of 5" kicker="First-time setup"
        title="Choose intake questions"
        lede="What does Ava always ask first-time customers? She saves answers to the lead profile so you have full context before they walk in."
        mock={<Ob5Intake />}
        instructions={[
          <>Toggle the questions you want Ava to always ask.</>,
          <>Tap the pencil to edit wording.</>,
          <>Tap <strong className="text-white">Continue</strong> — onboarding complete, dashboard unlocks.</>,
        ]}
        why="Captured intake data shows up in Leads → Lead Detail → Profile, so you can prep before they arrive. &quot;Sarah is here for balayage, allergic to ammonia, was referred by Jess&quot; — that level of context wins customers for life."
      />

      {/* ─── 4. DASHBOARD HOME ─── */}
      <Section
        n={4} kicker="Dashboard"
        title="Your control room"
        lede="Once setup is done you land on app.nextbell.ai. The home screen has 4 zones: sidebar (left), daily brief (top), KPIs (middle), and split panels for activity feed + alerts (bottom)."
        mock={<DashHomeMock />}
        instructions={[
          <>The <strong className="text-white">left sidebar</strong> is your map: Dashboard, Calls, Leads, Appointments, Messaging, Ava, Automations, Analytics, Settings. Each tab below has a full walkthrough.</>,
          <>The <strong className="text-white">daily brief</strong> auto-generates at 8am every day. AI summary of what happened overnight + what needs attention.</>,
          <>The <strong className="text-white">4 KPI tiles</strong> are clickable. Tap &quot;Calls today&quot; to jump straight to the Calls tab pre-filtered.</>,
          <>The <strong className="text-white">activity feed</strong> shows the last 4 actions Ava took on your behalf.</>,
          <>The <strong className="text-white">attention card</strong> highlights anything that needs YOU specifically — voicemails, FAQ gaps, integration problems.</>,
        ]}
        why="The home screen is built so you can glance at it for 30 seconds with your morning coffee and know exactly what Ava handled overnight, what to follow up on, and where leads are dropping off."
      />

      {/* ─── 5. CALLS ─── */}
      <Section
        n={5} kicker="Calls"
        title="Every conversation, transcribed and summarised"
        lede="The Calls tab is your voice command center. Top: 3 KPI tiles. Middle: every call as a card with outcome chip + AI summary. Bottom: filters, search, date range."
        mock={<CallsMock />}
        instructions={[
          <>Each card shows duration, outcome chip (Booked/Quoted/Voicemail/Transfer), caller name, phone, and Ava&apos;s 1-line summary.</>,
          <>Tap any card to open the detail drawer with audio recording, full transcript, and the Conversation replay timeline.</>,
          <>Use the top filter chips to narrow by date range, outcome, or search by name/phone.</>,
          <>The KPI strip shows total calls, minutes used vs. plan limit, and how many had recordings.</>,
        ]}
        connects={<>Every call here automatically creates a <strong>Lead</strong> if the caller is new, and an <strong>Appointment</strong> if Ava booked. Both show up under those tabs instantly.</>}
      />
      <Section
        sub="Inside the call drawer" kicker="Calls"
        title="Replay every word, every step"
        lede="When you tap a call, this drawer slides up. Audio player on top, transcript in the middle, and the new Conversation Replay at the bottom — a step-by-step timeline of the AI&apos;s decisions."
        mock={<CallReplayMock />}
        instructions={[
          <>Audio player: tap play, drag the seek bar. Speed up to 1.5× or 2× for fast review.</>,
          <>Tap <strong className="text-white">Conversation replay</strong> to expand the timeline.</>,
          <>Each marker shows: caller intent classified, which node Ava followed, what she said next.</>,
          <>The colored dots = blue (intent), purple (engine route), emerald (call ended).</>,
        ]}
        why="If a customer ever calls back saying &quot;Ava confused me about pricing&quot; — open this replay, see the EXACT path she took, the EXACT KB entry she quoted. Within 2 minutes you can fix the answer in Ava → Knowledge and the next caller hears it correctly."
        tip="The replay is gold for training your team. Watch 5 calls a week to spot patterns. You&apos;ll see Ava handle objections better than most humans within 30 days."
      />

      {/* ─── 6. LEADS ─── */}
      <Section
        n={6} kicker="Leads"
        title="Pipeline view — drag, drop, win"
        lede="The Leads tab shows your customer pipeline as 5 columns. Drag cards between columns to manually move leads. Or set up a Decision automation that does it automatically."
        mock={<LeadsBoardMock />}
        instructions={[
          <>5 columns by default: New, Contacted, Qualified, Booked, Won (configurable in Settings).</>,
          <>Each card shows the lead&apos;s name and AI lead score (0–100, color-coded red/amber/green).</>,
          <>Drag a card across columns to move it through your pipeline manually.</>,
          <>Tap any card to open the lead detail page (next mock).</>,
          <>The number under each column header is how many leads are in that stage.</>,
        ]}
      />
      <Section
        sub="Lead detail page" kicker="Leads"
        title="One customer, full story"
        lede="Tap any lead card to open this view. Profile + tags on the left, full timeline of every interaction (calls, SMS, bookings) on the right."
        mock={<LeadDetailMock />}
        instructions={[
          <>Profile card: name, phone, email, source. Click to edit any field inline.</>,
          <>Tags card: add tags like <em>hot</em>, <em>wedding-prospect</em>, <em>vip</em>. Tags drive automation rules.</>,
          <>Timeline: every action across the system, oldest first. Each line shows that Ava did it and a timestamp.</>,
          <>Add a manual note any time, it shows in the timeline as &quot;Note by Alex&quot;.</>,
        ]}
        connects={<>Tags here can trigger automations. Tag a lead <em>hot</em> and the &quot;Hot Lead Callback&quot; flow fires within 5 minutes (if you have it active).</>}
      />

      {/* ─── 7. MESSAGING ─── */}
      <Section
        n={7} kicker="SMS"
        title="2-way SMS in your browser"
        lede="When customers text your NextBell number, conversations land here in an iMessage-style inbox. Send/receive in real time. Ava drafts replies for you to approve, or you can take over."
        mock={<MessagingMock />}
        instructions={[
          <>Left rail: every conversation. Unread count badge on the right.</>,
          <>Tap a conversation to open the chat. Yours messages are blue (right), customer&apos;s are dark (left).</>,
          <>Type in the composer and Enter to send. Sent via your Twilio NextBell number.</>,
          <>Tap <strong className="text-purple-300">AI suggest</strong> to draft a reply using your KB tone.</>,
          <>Polls every 15 seconds for new messages — near real-time when this tab is open.</>,
        ]}
        why="93% of texts are read within 3 minutes. SMS is by far the highest-converting channel for confirmations, reminders, and quick replies. Bringing it inside the dashboard means you&apos;re never juggling tabs."
      />

      {/* ─── 8. APPOINTMENTS ─── */}
      <Section
        n={8} kicker="Calendar"
        title="Two-way calendar sync"
        lede="The Appointments tab is your unified calendar. Connect Google Calendar (Outlook coming soon). Ava books straight here, your team sees it instantly, conflicts are detected automatically."
        mock={<AppointmentsMock />}
        instructions={[
          <>Top right: switch between Week, Month, Agenda views.</>,
          <>Click any empty slot to manually create an appointment.</>,
          <>Click any existing appointment to view details, reschedule, cancel, or send a manual reminder.</>,
          <>Color-coded by service type, set in Settings → Business.</>,
        ]}
        why="The calendar is the source of truth. Ava reads it before quoting times to a caller (so she never double-books), then writes back to it when she confirms. Two-way sync with Google Calendar."
      />

      {/* ─── 9–11. AVA TABS ─── */}
      <Section
        n={9} kicker="Ava brain"
        title="Identity tab — how Ava sounds"
        lede="The Ava tab is split into 3 sub-tabs. Identity is the first: name, voice, the locked branded greeting, and any special instructions you want her to follow on every call."
        mock={<AvaIdentityMock />}
        instructions={[
          <><strong className="text-white">AI Name</strong>: rename Ava to anything (Iris, Sage, Echo). The change shows up in the locked greeting on the next call.</>,
          <><strong className="text-white">Voice</strong>: pick from 4 high-quality voices. Each voice has a name + warmth/style indicator.</>,
          <><strong className="text-white">Branded greeting</strong>: locked so Ava can&apos;t paraphrase it. The business name auto-fills from your profile.</>,
          <><strong className="text-white">Special instructions</strong>: free-text field. Type rules like &quot;Never quote prices over $500 without offering a free consultation.&quot; Ava follows them in addition to her regular logic.</>,
          <>Tap <strong className="text-blue-300">Test Ava on my phone</strong> top-right, hear the changes live in 5 seconds.</>,
        ]}
      />
      <Section
        n={10} kicker="Ava brain"
        title="Knowledge Base — what Ava knows"
        lede="The Knowledge tab is Ava&apos;s memory. Three input types: Q&A pairs, free-form notes, and PDFs. Ava searches everything in real time during calls and quotes the best match."
        mock={<AvaKnowledgeMock />}
        instructions={[
          <>Tap <strong className="text-blue-300">+ Add Q&A</strong> for the most common type — &quot;What are your hours?&quot; → &quot;9–7, closed Sunday&quot;.</>,
          <>Tap <strong className="text-amber-300">+ Add note</strong> for policies, procedures, anything free-form (cancellation policy, dress code, parking).</>,
          <>Tap <strong className="text-red-300">Upload PDF</strong> for menus, price lists, brochures up to 10MB. Ava chunks and indexes them automatically.</>,
          <>Each entry shows a confidence score (how often Ava finds it relevant). Low score = rewrite the entry.</>,
          <>Use the &quot;Try a question&quot; box at the bottom to preview what Ava would answer with your current KB — instant feedback.</>,
        ]}
        connects={<>Knowledge feeds Ava during calls (audio) and is what the <strong>Lookup KB</strong> node in Automations reads from. One source, every channel.</>}
        tip="Aim for 20-30 entries before going live. Cover: hours, location, parking, policies, top 5 services with prices, &quot;do you do X&quot; for things you DON&apos;T do (so Ava politely declines), refund policy, manager email."
      />
      <Section
        n={11} kicker="Ava brain"
        title="Playbook — how Ava runs each call"
        lede="The Playbook tab links to your during-call flow + shows the version history. You can roll back to any previous version with one tap (we auto-snapshot before every rollback)."
        mock={<AvaPlaybookMock />}
        instructions={[
          <>The top card shows your active during-call playbook. Tap <strong className="text-blue-300">Edit in Flow editor</strong> to open the visual builder.</>,
          <>The versions card lists your last 20 snapshots — manual, auto, and pre-rollback labels.</>,
          <>Tap <strong className="text-blue-300">Snapshot now</strong> before making big changes — gives you a safe restore point.</>,
          <>Tap <strong className="text-blue-300">Roll back</strong> on any version → confirm. Ava uses the restored playbook on the very next call.</>,
        ]}
        why="Playbook changes are real-time. If you tweak something at 2pm and your 2:05pm caller hears the new behaviour, that&apos;s great when you&apos;re right and stressful when you&apos;re wrong. Versions = safety net."
      />

      {/* ─── 12–15. AUTOMATIONS ─── */}
      <Section
        n={12} kicker="Automations"
        title="Templates — start here"
        lede="The Automations tab opens with the template picker. 17 ready-made flows organised by industry and use-case. Click one to drop it onto a fresh canvas, customize, and activate."
        mock={<FlowTemplatesMock />}
        instructions={[
          <>Browse the 6 most-used templates on the front page.</>,
          <>Each card shows: icon, name, tag (Recommended/Salons/AI/Loyalty/etc.), and a 1-line description.</>,
          <>Tap any card to instantly create a new flow pre-filled with that template — saves 10-30 minutes vs. starting from scratch.</>,
          <>Or tap <strong className="text-purple-300">Generate with AI</strong> to type a sentence and have GPT build the flow for you (next section).</>,
          <>Or tap <strong className="text-blue-300">+ New flow</strong> for a blank canvas.</>,
        ]}
        why="Templates exist because every salon needs the same Missed-Call→SMS flow, every clinic needs the same Reminder Cascade. Start from a known-good baseline, customize the wording, ship."
      />
      <Section
        n={13} kicker="Automations"
        title="Flow editor — the visual canvas"
        lede="Click into any flow and you land here. Top toolbar: Active toggle, Test fire, AI Review, Save. Add toolbar: drag node types onto the canvas. Canvas: nodes, edges, drag-pan, scroll-zoom."
        mock={<FlowCanvasMock />}
        instructions={[
          <>Toggle <strong className="text-blue-300">Active</strong> top right to turn the flow on. Inactive flows don&apos;t fire even if events happen.</>,
          <>Use <strong className="text-purple-300">Test fire</strong> to fire the trigger event with sample data — see the flow run end-to-end without waiting for a real customer.</>,
          <>Tap <strong className="text-blue-300">AI Review</strong> after the flow has run a few times — GPT analyses run history and suggests tweaks.</>,
          <>The <strong>ADD:</strong> bar lets you drop new nodes: Stage (pipeline column), Trigger (start), Action (do something), Agent (AI), Decision (branch), and conversation nodes (Ava says, Ava asks, Lookup KB).</>,
          <>Drag from one node&apos;s right edge to the next node&apos;s left edge to wire them together.</>,
          <>Click any node to open the inspector — configure SMS templates, wait durations, decision branches, etc.</>,
        ]}
        tip="The Master Workflow template is the easiest place to start. It connects EVERY dashboard concept on one canvas (call → Ava → branch → pipeline → SMS) so you can see how the whole system fits together at a glance."
      />
      <Section
        n={14} kicker="Automations"
        title="Generate with AI"
        lede="Stuck staring at a blank canvas? Type a sentence and our AI builds the flow for you in 3 seconds. It picks the right trigger, drops the right action nodes, wires them up, and sets sensible defaults."
        mock={<FlowAIGenMock />}
        instructions={[
          <>Tap <strong className="text-purple-300">Generate with AI</strong> from the templates page or from any flow editor.</>,
          <>Type your goal in plain English. The more specific, the better.</>,
          <>(Optional) Tap suggestion chips like &quot;Friendly tone&quot;, &quot;Cite KB&quot; to refine.</>,
          <>Tap <strong className="text-purple-300">Generate flow</strong>. Wait ~3 seconds.</>,
          <>The canvas fills up with your nodes pre-wired. Edit anything you want, then activate.</>,
        ]}
        why="Most customers don&apos;t know they want an automation until they describe a frustration in plain English. Letting them type the frustration and getting a working flow back closes the gap from idea to live in under 60 seconds."
      />
      <Section
        n={15} kicker="Automations"
        title="Run replay — see what happened"
        lede="Every time a flow fires, it logs every node, every wait, every decision. Open any past run from the bottom drawer to see the step-by-step replay with timestamps."
        mock={<FlowReplayMock />}
        instructions={[
          <>From the flow editor, scroll down or tap <strong className="text-blue-300">Run history</strong> — it lists the last 50 runs.</>,
          <>Click any run to expand the step-by-step timeline.</>,
          <>Each step shows: type chip (Trigger/Agent/Decision/Action), what happened, timestamp from start.</>,
          <>Errors are highlighted red. Hover for the full error message.</>,
        ]}
        why="When something goes wrong (an SMS didn&apos;t send, a lead didn&apos;t move stage), the replay tells you exactly which step failed and why. Most fixes take less than 60 seconds once you can SEE the issue."
      />

      {/* ─── 16–18. ANALYTICS ─── */}
      <Section
        n={16} kicker="Analytics"
        title="KPIs + calls-per-day chart"
        lede="The Analytics tab opens with 4 headline KPIs and a 14-day calls bar chart. Switch the date range with the chips top right."
        mock={<AnalyticsKPIsMock />}
        instructions={[
          <>4 KPIs: Total calls, Answered (rate), Booked (rate), Revenue. Each shows period-over-period delta.</>,
          <>The chart below shows daily call volume over the last 14 days.</>,
          <>Switch date range: Last 30 days (default), 90 days, All time.</>,
          <>Hover any bar for the exact daily count.</>,
        ]}
      />
      <Section
        n={17} kicker="Analytics"
        title="Lead sources & top services"
        lede="Two side-by-side breakdowns. Left: where leads come from (so you know which marketing channel is paying off). Right: which services drive most volume + revenue this month."
        mock={<AnalyticsSourcesMock />}
        instructions={[
          <>Left: stacked bar of lead sources — inbound calls, web form, SMS, referrals.</>,
          <>Tap any source to drill into the leads from that channel.</>,
          <>Right: top 5 services with count and revenue, sorted by revenue.</>,
        ]}
        why="Knowing 42% of leads come from inbound calls means the receptionist (Ava) IS your funnel. Knowing balayage drives 47% of revenue from 13% of bookings means promote balayage harder."
      />
      <Section
        n={18} kicker="Analytics"
        title="Peak-hours heatmap"
        lede="A 6-day × 12-hour grid showing call density. Darker = more calls. Use it to staff smarter, schedule promos, or open earlier on busy days."
        mock={<AnalyticsHeatmapMock />}
        instructions={[
          <>Each cell = one hour on one day. Number = calls in that hour.</>,
          <>Color scale: from light (1-2 calls) to deep blue (7+ calls).</>,
          <>Insight bar at the bottom auto-detects your peak slot and suggests an action.</>,
        ]}
        connects={<>If your peak hour is Saturday 11am and Ava is your only receptionist on Saturdays — congrats, Ava just saved you a hire.</>}
      />

      {/* ─── 19–22. SETTINGS ─── */}
      <Section
        n={19} kicker="Settings"
        title="Profile + 2FA + sessions"
        lede="The Settings tab has 6 sub-tabs. Profile is first: your name, email, password, 2FA toggle, and a list of every active session (each device that&apos;s logged in)."
        mock={<SettingsProfileMock />}
        instructions={[
          <>Edit name, email, password, phone any time. Email change requires confirmation from BOTH old + new addresses.</>,
          <>Toggle <strong className="text-blue-300">Two-factor authentication</strong> on for extra security.</>,
          <>Active sessions: see every device signed in. Sign out individuals or use <strong>Sign out everywhere else</strong> (handy if you lose a device).</>,
        ]}
        tip="If you ever lose your phone or laptop, sign in from another device, scroll to Profile, tap Sign out everywhere else. Every other JWT is invalidated immediately, even if the bad actor still has the cookie."
      />
      <Section
        n={20} kicker="Settings"
        title="Business details"
        lede="Business name, industry, phone, address, hours, services. Edits here flow into Ava&apos;s prompt automatically."
        mock={<SettingsBusinessMock />}
        instructions={[
          <>Top: 4 fields for name, industry, phone, address. Inline edit.</>,
          <>Hours grid: tap a day to toggle open/closed. Tap the time to edit.</>,
          <>Services list: tap pencil to edit, trash to delete, <strong className="text-blue-300">+ Add service</strong> for new ones.</>,
        ]}
        connects={<>Hours feed: Ava&apos;s booking logic, the Appointments calendar, SMS reminder timing. Services feed: Ava&apos;s pricing answers, the Appointments dropdown, Analytics breakdowns.</>}
      />
      <Section
        n={21} kicker="Settings"
        title="Billing — plan, usage, refund"
        lede="Plan card on top, usage tiles below, payment method, and (if eligible) a self-serve refund button."
        mock={<SettingsBillingMock />}
        instructions={[
          <>Plan card shows current plan, renewal date, trial days left.</>,
          <><strong className="text-white">Change plan</strong> upgrades/downgrades instantly. Pro-rates the difference.</>,
          <>Usage tiles: voice minutes, SMS, knowledge entries — current vs. plan limit.</>,
          <>Payment method: tap Change to swap card.</>,
          <>If no minutes/SMS used AND within 7 days of payment, the green <strong className="text-blue-300">Request refund</strong> button auto-appears. One click, full refund within 24h. After that, cancel anytime in the Stripe portal to stop the next renewal.</>,
        ]}
      />
      <Section
        n={22} kicker="Settings"
        title="API access"
        lede="If you're on Agency plan, this tab gives you a personal API key + webhooks to wire NextBell into anything you build."
        mock={<SettingsApiMock />}
        instructions={[
          <>Tap <strong className="text-blue-300">Generate key</strong> to mint a new API key. Old keys keep working until you revoke them.</>,
          <>Copy the key once — we never show it again. Store it in a password manager.</>,
          <>Set up webhooks: subscribe to events like <em>call.completed</em> or <em>lead.created</em>. We POST a JSON payload to your URL within seconds of the event.</>,
          <>The full API docs are linked at the bottom of the page.</>,
        ]}
      />

      {/* ─── 23. TEST CALL ─── */}
      <Section
        n={23} kicker="The big moment"
        title="Test Ava on your phone"
        lede="Before you forward your real business line to Ava, you should hear her live. The Test Ava button on the Ava tab calls YOUR phone using your current playbook + KB + identity."
        mock={<TestCallMock />}
        instructions={[
          <>Go to <strong className="text-white">Ava</strong> tab → tap the green <strong className="text-blue-300">Test Ava on my phone</strong> button.</>,
          <>Type your phone number in E.164 format (e.g. +14155551234).</>,
          <>Tap <strong className="text-blue-300">Call me</strong>.</>,
          <>Within 5 seconds, your phone rings. Caller ID shows your NextBell number.</>,
          <>Pick up. Ask 3 things: business hours, a service price, and &quot;I&apos;d like to book&quot;.</>,
          <>Hang up. Within 30 seconds the call shows in your Calls tab with full transcript and replay.</>,
        ]}
        why="There&apos;s no better way to validate your setup than calling yourself. If Ava nails it, you&apos;re ready to go live. If she stumbles, you know exactly what to add to the Knowledge Base."
        tip="Have your spouse, your kid, and your most demanding customer test her too. Each fresh person surfaces edge cases you&apos;d never think of yourself."
      />

      {/* ─── 24. HOW IT CONNECTS ─── */}
      <section className="py-16 sm:py-20 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <StepBadge n={24} />
          <SectionHeader
            kicker="The big picture"
            title="How everything connects"
            lede="One customer journey, every screen working together. Every box below is a tab in your dashboard, every arrow is data flowing automatically — no manual hand-offs."
          />
          <HowItConnectsDiagram />
        </div>
      </section>

      {/* ─── 25. LAUNCH CHECKLIST ─── */}
      <section className="py-20 sm:py-24 px-5 sm:px-8 border-t border-white/[0.04]">
        <div className="max-w-4xl mx-auto">
          <StepBadge n={25} />
          <SectionHeader
            kicker="Going live"
            title="The launch checklist"
            lede="The last mile. 8 items, ~30 minutes total. Once these are green, point your real business line at NextBell and start banking your time back."
          />
          <GlassPanel className="p-6 sm:p-8">
            <ul className="space-y-3">
              {[
                { t: 'Identity is set', d: 'Ava → Identity → tone selected, special instructions saved, voice picked.' },
                { t: 'Knowledge has 20+ entries', d: 'Ava → Knowledge → cover hours, location, parking, top services, refund policy, FAQs.' },
                { t: 'Active during-call playbook', d: 'Ava → Playbook → at least one flow with trigger=call.answered is on.' },
                { t: 'Business hours match reality', d: 'Settings → Business → check every day, including this week\'s holidays.' },
                { t: 'Services & pricing entered', d: 'Settings → Business → every service you charge for, with starting price + duration.' },
                { t: 'Ran a Test Call successfully', d: 'Ava tab → Test Ava on my phone → completed at least one full conversation.' },
                { t: 'Reviewed at least 3 calls', d: 'Calls tab → open each call → check transcript and replay. Fix any KB gaps you spot.' },
                { t: 'Forwarded your business line', d: 'In your phone provider, set call forwarding to your NextBell number. You\'re live.' },
              ].map((it, i) => (
                <li key={i} className="flex items-start gap-3 bg-white/[0.025] border border-white/[0.06] rounded-xl p-4">
                  <span className="w-6 h-6 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-blue-300" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-white text-[14px] font-semibold">{it.t}</p>
                    <p className="text-slate-400 text-[12.5px] mt-0.5 leading-relaxed">{it.d}</p>
                  </div>
                </li>
              ))}
            </ul>
          </GlassPanel>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="py-24 sm:py-28 px-5 sm:px-8 border-t border-white/[0.04] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-blue-500/15 blur-[140px]" />
        </div>
        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-5xl font-medium leading-tight mb-5">You&apos;re ready.<br /><span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">Ava is waiting for the first call.</span></h2>
          <p className="text-slate-400 text-base sm:text-lg leading-relaxed mb-8 max-w-2xl mx-auto">
            From signup to first booked call, most customers are live in under 45 minutes. The setup is one-time, the upside is forever.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button onClick={() => navigate('/signup')} className="bg-white text-black hover:bg-slate-100 font-medium text-[14px] px-6 py-3 rounded-full inline-flex items-center gap-2">
              Start free trial <ArrowRight className="w-4 h-4" />
            </button>
            <a href="mailto:sales@nextbell.ai" className="border border-white/15 hover:bg-white/[0.04] text-white font-medium text-[14px] px-6 py-3 rounded-full inline-flex items-center gap-2">
              Book a 15-min demo
            </a>
          </div>
          <p className="text-[12px] text-slate-500 mt-6">Need help? <a href="mailto:support@nextbell.ai" className="text-slate-300 hover:text-white">support@nextbell.ai</a> · We answer in &lt; 1 hour during business hours.</p>
        </div>
      </section>
    </div>
  );
}
